import { supabase, SUPABASE_URL } from './supabase';
import { todayISO, localISO } from './utils/attendanceDate';

// ─── SCHOOL ───────────────────────────────────────────────
export async function getSchool() {
  const { data, error } = await supabase.from('schools').select('*');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('No school found');
  return data[0];
}

// ─── SETTINGS ────────────────────────────────────────────
export async function getSettings(schoolId) {
  const { data, error } = await supabase.from('settings').select('key, value').eq('school_id', schoolId);
  if (error) throw error;
  const result = {};
  (data || []).forEach(s => { result[s.key] = s.value; });
  return result;
}

export async function saveSetting(schoolId, key, value, userId) {
  const { error } = await supabase.from('settings').upsert(
    { school_id: schoolId, key, value, updated_by: userId, updated_at: new Date().toISOString() },
    { onConflict: 'school_id,key' }
  );
  if (error) throw error;
}

// ─── GRADES ──────────────────────────────────────────────
export async function getGrades(schoolId) {
  const { data, error } = await supabase.from('grades').select('*').eq('school_id', schoolId).order('order_index');
  if (error) throw error;
  return data;
}

// ─── CLASSES ─────────────────────────────────────────────
export async function getClasses(schoolId) {
  const { data, error } = await supabase
    .from('classes')
    .select('id, name, code, order_num, is_active, sections(id,name)')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('order_num');
  if (error) throw error;
  return data || [];
}

// ─── FAMILIES ────────────────────────────────────────────
export async function getFamilies(schoolId) {
  const { data, error } = await supabase.from('families').select('*').eq('school_id', schoolId);
  if (error) throw error;
  return data;
}

export async function saveFamily(family) {
  const { data, error } = await supabase.from('families').upsert(family).select().single();
  if (error) throw error;
  return data;
}

// ─── STUDENTS ────────────────────────────────────────────

// Maps a raw Supabase student row into the shape the app expects.
// Supabase returns student_fee_assignments as an array of { fee_head_id, amount, is_active }.
// The app reads student.fees as { [fee_head_id]: amount }.
export function mapDbStudent(row) {
  const fam = row.families || {};
  const fees = {};
  (row.student_fee_assignments || [])
    .filter(a => a.is_active !== false)
    .forEach(a => { fees[a.fee_head_id] = Number(a.amount || 0); });

  return {
    id:                row.id,
    admissionNo:       row.admission_no,
    roll:              row.roll_no,
    name:              row.name,
    class:             row.grades?.name || row.grade_id || '',
    section:           row.sections?.name || '',
    status:            row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1).replace(/_/g, ' ') : 'Active',
    gender:            row.gender ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : '',
    dob:               row.dob || '',
    religion:          row.religion || '',
    nationality:       row.nationality || '',
    cast:              row.caste || '',
    bform:             row.bform_no || '',
    bloodGroup:        row.blood_group || '',
    allergies:         row.allergies || '',
    conditions:        row.conditions || '',
    disability:        row.disability || '',
    vaccination:       row.vaccination_status || '',
    doctorName:        row.doctor_name || '',
    doctorContact:     row.doctor_contact || '',
    previousSchool:    row.previous_school || '',
    lastClassPassed:   row.last_class_passed || '',
    tcNo:              row.tc_no || '',
    reasonLeaving:     row.reason_leaving_prev || '',
    admissionDate:     row.admission_date || '',
    leavingDate:       row.leaving_date || '',
    leavingReason:     row.leaving_reason || '',
    discountType:      row.discount_type || '',
    discountAmount:    Number(row.discount_amount || 0),
    notes:             row.notes || '',
    counsellingNotes:  row.counselling_notes || '',
    photoData:         row.photo_url || null,
    photoUrl:          row.photo_url || null,
    // Family fields flattened
    familyId:          fam.family_id_display || '',
    familyUuid:        fam.id || row.family_id || '',
    fatherName:        fam.father_name || '',
    fatherCnic:        fam.father_cnic || '',
    fatherOccupation:  fam.father_occupation || '',
    fatherEducation:   fam.father_education || '',
    fatherIncome:      fam.father_income || '',
    motherName:        fam.mother_name || '',
    motherCnic:        fam.mother_cnic || '',
    motherOccupation:  fam.mother_occupation || '',
    guardianName:      fam.guardian_name || '',
    guardianRelation:  fam.guardian_relation || '',
    guardianCnic:      fam.guardian_cnic || '',
    phone:             fam.phone || '',
    whatsapp:          fam.whatsapp || '',
    emergencyContact:  fam.emergency_contact || '',
    address:           fam.address || '',
    area:              fam.area || '',
    // Fee assignments — the key field the rest of the app reads
    fees,
  };
}

export async function getStudents(schoolId) {
  const { data, error } = await supabase
    .from('students')
    .select('*, families(*), grades(name,code), sections(name), student_fee_assignments(fee_head_id, amount, is_active)')
    .eq('school_id', schoolId)
    .order('admission_no');
  if (error) throw error;
  return (data || []).map(mapDbStudent);
}

export async function saveStudent(student) {
  const { data, error } = await supabase.from('students').upsert(student).select().single();
  if (error) throw error;
  return data;
}

const STUDENT_FK_TABLES = [
  'student_documents', 'student_history', 'attendance', 'leave_requests',
  'homework_submissions', 'exam_results',
  'challan_items', 'payment_items', 'fee_waivers', 'advance_ledger',
  'arrears_ledger', 'sales', 'discount_ledger', 'mark_entries',
  'student_vaccinations',
];

export async function canHardDeleteStudent(studentId) {
  const results = await Promise.all(
    STUDENT_FK_TABLES.map(table =>
      supabase.from(table).select('id', { count: 'exact', head: true }).eq('student_id', studentId)
    )
  );
  const blockingTables = STUDENT_FK_TABLES.filter((_, i) => (results[i].count || 0) > 0);
  return { canDelete: blockingTables.length === 0, blockingTables };
}

export async function deleteStudent(id) {
  await supabase.from('student_fee_assignments').delete().eq('student_id', id);
  await supabase.from('fee_assignments').delete().eq('student_id', id);
  const { error } = await supabase.from('students').delete().eq('id', id);
  if (error) throw error;
}

export async function updateStudentStatus(id, status) {
  const { error } = await supabase.from('students').update({ status, updated_at: new Date() }).eq('id', id);
  if (error) throw error;
}

// ─── ADMISSION & FAMILY ID ───────────────────────────────
export async function getNextAdmissionNo(schoolId, format) {
  const fmt = format || 'ADM-####';
  // Highest trailing number across the whole school, not the most recently created row:
  // bulk imports create rows in one instant, so "latest" is arbitrary and can sit below the real maximum.
  // limit() stops PostgREST's 1000-row default from truncating the list.
  const { data } = await supabase
    .from('students')
    .select('admission_no')
    .eq('school_id', schoolId)
    .limit(20000);
  if (!data || data.length === 0) {
    return fmt.replace(/#+/g, m => '1'.padStart(m.length, '0'));
  }
  let highest = 0;
  for (const row of data) {
    const digits = String(row.admission_no || '').match(/\d+$/);
    if (digits) highest = Math.max(highest, parseInt(digits[0], 10));
  }
  return fmt.replace(/#+/g, m => String(highest + 1).padStart(m.length, '0'));
}

export async function getNextFamilyId(schoolId, format) {
  const { data } = await supabase
    .from('families')
    .select('family_id_display')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (!data || data.length === 0) return format.replace(/#+/g, m => '1'.padStart(m.length, '0'));
  const last = data[0].family_id_display || '';
  const num = parseInt(last.replace(/\D/g, '')) || 0;
  return format.replace(/#+/g, m => String(num + 1).padStart(m.length, '0'));
}

// ─── FEE HEADS ───────────────────────────────────────────
export async function getFeeHeads(schoolId) {
  const { data, error } = await supabase.from('fee_heads').select('*').eq('school_id', schoolId).eq('is_active', true).order('order_index');
  if (error) throw error;
  return data;
}

export async function saveFeeHead(feeHead) {
  const { data, error } = await supabase.from('fee_heads').upsert(feeHead).select().single();
  if (error) throw error;
  return data;
}

export async function deleteFeeHead(id) {
  const { error } = await supabase.from('fee_heads').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

// ─── FEE STRUCTURES ──────────────────────────────────────
export async function getFeeStructures(schoolId) {
  const { data, error } = await supabase
    .from('fee_structures')
    .select('id, class_id, grade_id, classes(name), grades(name), is_active, fee_structure_items(id, fee_head_id, amount)')
    .eq('school_id', schoolId)
    .eq('is_active', true);
  if (error) throw error;
  const map = {};
  for (const s of data || []) {
    // Prefer class_id (new) over grade_id (legacy)
    const key = s.classes?.name || s.grades?.name || s.class_id || s.grade_id;
    if (!key) continue;
    map[key] = {};
    for (const it of (s.fee_structure_items || [])) {
      map[key][it.fee_head_id] = Number(it.amount) || 0;
    }
  }
  return map;
}

export async function saveFeeStructure(schoolId, className, amounts, userId) {
  // Look up in the classes table (the school's actual classes)
  const { data: cls } = await supabase
    .from('classes').select('id').eq('school_id', schoolId).eq('name', className).maybeSingle();
  const classId = cls?.id;
  if (!classId) throw new Error(`Class not found: ${className}`);

  // Find existing fee structure for this class, or create one
  let { data: existing } = await supabase
    .from('fee_structures').select('id').eq('school_id', schoolId).eq('class_id', classId).maybeSingle();
  let structureId;
  if (existing) {
    structureId = existing.id;
  } else {
    const { data: created, error: createErr } = await supabase
      .from('fee_structures')
      .insert({ school_id: schoolId, class_id: classId, name: className, is_active: true })
      .select('id').single();
    if (createErr) throw createErr;
    structureId = created.id;
  }

  await supabase.from('fee_structure_items').delete().eq('fee_structure_id', structureId);
  const rows = Object.entries(amounts)
    .filter(([, v]) => Number(v) > 0)
    .map(([feeHeadId, amount]) => ({ fee_structure_id: structureId, fee_head_id: feeHeadId, amount: Number(amount), school_id: schoolId }));
  if (rows.length > 0) {
    const { error: insErr } = await supabase.from('fee_structure_items').insert(rows);
    if (insErr) throw insErr;
  }
}

// ─── STUDENT FEE ASSIGNMENTS ─────────────────────────────
export async function getStudentFees(studentId) {
  const { data, error } = await supabase
    .from('student_fee_assignments')
    .select('*, fee_heads(name,type)')
    .eq('student_id', studentId)
    .eq('is_active', true);
  if (error) throw error;
  return data;
}

export async function saveStudentFees(studentId, schoolId, feeRows) {
  // feeRows: [{ fee_head_id, amount, discount_type?, discount_value? }]
  const today = todayISO();
  await supabase.from('student_fee_assignments').update({ is_active: false }).eq('student_id', studentId).eq('is_active', true);
  if (feeRows.length > 0) {
    const { error } = await supabase.from('student_fee_assignments').insert(
      feeRows.map(r => ({
        student_id: studentId,
        school_id: schoolId,
        fee_head_id: r.fee_head_id,
        amount: r.amount,
        discount_type: r.discount_type || null,
        discount_value: r.discount_type ? (r.discount_value || 0) : 0,
        is_active: true,
        effective_from: today,
      }))
    );
    if (error) throw error;
  }
}

export async function getStudentVaccinations(studentId) {
  const { data, error } = await supabase
    .from('student_vaccinations')
    .select('id, dose_name, given_at')
    .eq('student_id', studentId)
    .order('given_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addStudentVaccination(schoolId, studentId, doseName) {
  const { error } = await supabase.from('student_vaccinations').insert({
    school_id: schoolId,
    student_id: studentId,
    dose_name: doseName,
    given_at: todayISO(),
  });
  if (error) throw error;
}

export async function removeStudentVaccination(id) {
  const { error } = await supabase.from('student_vaccinations').delete().eq('id', id);
  if (error) throw error;
}

export async function setFeeHeadDiscount(studentId, feeHeadId, discountType, discountValue) {
  const { error } = await supabase.from('student_fee_assignments')
    .update({
      discount_type: discountType || null,
      discount_value: discountType ? discountValue : 0,
    })
    .eq('student_id', studentId)
    .eq('fee_head_id', feeHeadId)
    .eq('is_active', true);
  if (error) throw error;
}

// ─── CHALLANS ────────────────────────────────────────────
// Reserves a block of challan numbers from a persistent per-school+month+year
// counter and returns the first. Replaces deriving the number from COUNT(*) of
// existing challans, which had two failure modes:
//   1. Two users generating at once both counted the same total and both started
//      at the same number — duplicate challan numbers on real documents.
//   2. Worse and not dependent on timing: deleting a challan lowered the count, so
//      the next generated number duplicated one already issued. Any deletion
//      corrupted the sequence.
// The counter only ever increments, so deletions no longer affect it, and the
// reservation is a single atomic statement so concurrent callers get separate
// blocks. Signature is unchanged; count defaults to 1 for single-challan callers.
export async function getNextChallanNo(schoolId, month, year, count = 1) {
  const { data, error } = await supabase.rpc('reserve_challan_numbers', {
    p_school_id: schoolId, p_month: month, p_year: year, p_count: count,
  });
  if (error) throw error;
  return data;  // caller formats with buildChallanNo(month, year, seq)
}

// ─── MULTI-CAMPUS ────────────────────────────────────────
// Campuses (schools of one organization) this user may open; is_active marks the current one.
export async function getMyCampuses() {
  const { data, error } = await supabase.rpc('my_campuses');
  if (error) throw error;
  return data;
}

// Moves the user's ACTIVE campus in the database. Callers go through services/campusSwitch.js.
export async function switchCampus(schoolId) {
  const { data, error } = await supabase.rpc('switch_campus', { p_school_id: schoolId });
  if (error) throw error;
  return data;
}

// One dashboard_summary per campus (no defaulters). Empty for roles that may not see other campuses.
export async function getCampusSummaries(orgId = null) {
  const { data, error } = await supabase.rpc('campus_summaries', { p_org_id: orgId });
  if (error) throw error;
  return data;
}

export async function setCampusMembership(userId, schoolId, roleName) {
  const { data, error } = await supabase.rpc('set_campus_membership', { p_user_id: userId, p_school_id: schoolId, p_role_name: roleName });
  if (error) throw error;
  return data;
}

export async function removeCampusMembership(userId, schoolId) {
  const { data, error } = await supabase.rpc('remove_campus_membership', { p_user_id: userId, p_school_id: schoolId });
  if (error) throw error;
  return data;
}

export async function getUserCampuses(userId) {
  const { data, error } = await supabase.rpc('user_campuses', { p_user_id: userId });
  if (error) throw error;
  return data;
}

export async function getDashboardSummary(schoolId, month = null, year = null) {
  const { data, error } = await supabase.rpc('dashboard_summary', { p_school_id: schoolId, p_month: month, p_year: year });
  if (error) throw error;
  return data;
}

// Dashboard rows only carry the display id (e.g. FAM-0007). This returns the family uuid and its phone numbers.
export async function getFamilyContact(schoolId, displayId) {
  const { data, error } = await supabase
    .from('families')
    .select('id, family_id_display, father_name, phone, whatsapp')
    .eq('school_id', schoolId)
    .eq('family_id_display', displayId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Families that still owe something in the latest fee period (arrears included). Drives the sidebar badge.
export async function getFeePendingCount(schoolId) {
  const { data, error } = await supabase.rpc('fee_pending_count', { p_school_id: schoolId });
  if (error) throw error;
  return Number(data || 0);
}

export async function getChallans(schoolId, month, year) {
  const PAGE = 1000;
  let from = 0;
  let all = [];
  while (true) {
    const { data, error } = await supabase
      .from('challans')
      .select('*, families(*), challan_items(*, students(name,admission_no), fee_heads(name)), users!challans_generated_by_fkey(name), payments(*, payment_items(*))')
      .eq('school_id', schoolId)
      .eq('month', month + 1)
      .eq('year', year)
      .order('challan_no', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}


export async function getChallansById(ids) {
  if (!ids?.length) return [];
  const { data, error } = await supabase
    .from('challans')
    .select('id, challan_no, family_id, month, year, months_count, due_date, net_payable, paid_amount, status, families(*), challan_items(id, student_id, description, amount, net_amount, voided, item_type, students(id, name))')
    .in('id', ids);
  if (error) throw error;
  return data || [];
}

export async function getChallansForMonth(schoolId, month, year) {
  const { data, error } = await supabase
    .from('challans')
    .select('*')
    .eq('school_id', schoolId)
    .eq('month', month + 1)
    .eq('year', year)
    .order('challan_no');
  if (error) throw error;
  return data || [];
}

export async function generateChallan(challan, items) {
  const { data, error } = await supabase.from('challans').insert(challan).select().single();
  if (error) throw error;
  let insertedItems = [];
  if (items && items.length > 0) {
    const { data: itemData, error: itemError } = await supabase.from('challan_items').insert(
      items.map(i => ({ ...i, challan_id: data.id, school_id: challan.school_id }))
    ).select();
    if (itemError) throw itemError;
    insertedItems = itemData || [];
  }
  return { ...data, challan_items: insertedItems };
}

// Bulk insert many challan headers in ONE call instead of one insert per challan.
// Returns the inserted rows (with generated ids) — caller must correlate them
// back to source data using challan_no, since bulk insert return order is not
// guaranteed to match input order.
export async function generateChallansBatch(challans) {
  if (!challans || !challans.length) return [];
  const { data, error } = await supabase.from('challans').insert(challans).select();
  if (error) throw error;
  return data || [];
}

// Bulk insert challan_items for many challans in ONE call instead of one
// insert per challan.
export async function insertChallanItemsBatch(items) {
  if (!items || !items.length) return [];
  const { data, error } = await supabase.from('challan_items').insert(items).select();
  if (error) throw error;
  return data || [];
}

export async function updateChallanStatus(id, status, paidAmount) {
  const { error } = await supabase.from('challans').update({ status, paid_amount: paidAmount, updated_at: new Date() }).eq('id', id);
  if (error) throw error;
}

export async function approveChallans(schoolId, month, year, approvedBy) {
  const { error } = await supabase
    .from('challans')
    .update({ status: 'published', approved_by: approvedBy, approved_at: new Date().toISOString() })
    .eq('school_id', schoolId)
    .eq('month', month + 1)
    .eq('year', year)
    .eq('status', 'draft');
  if (error) throw error;
}

export async function deleteDraftChallans(schoolId, month, year) {
  const { data: draftChallans, error: fetchErr } = await supabase
    .from('challans')
    .select('id')
    .eq('school_id', schoolId)
    .eq('month', month + 1)
    .eq('year', year)
    .eq('status', 'draft');
  if (fetchErr) throw fetchErr;
  const ids = (draftChallans || []).map(c => c.id);
  if (!ids.length) return;
  const { error: itemsErr } = await supabase.from('challan_items').delete().in('challan_id', ids);
  if (itemsErr) throw itemsErr;
  const { error: chErr } = await supabase.from('challans').delete().in('id', ids);
  if (chErr) throw chErr;
}

export async function getUnpaidItems(schoolId) {
  const PAGE = 1000;
  let from = 0;
  let all = [];
  while (true) {
    const { data, error } = await supabase
      .rpc('get_unpaid_items', { p_school_id: schoolId })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

// Scoped version — fetches unpaid items for specific families only.
// Used by printChallans so a 5-family print job doesn't query the whole school.
export async function getUnpaidItemsForFamilies(schoolId, familyIds) {
  if (!familyIds || !familyIds.length) return [];
  const PAGE = 1000;
  let from = 0;
  let all = [];
  while (true) {
    const { data, error } = await supabase
      .rpc('get_unpaid_items', { p_school_id: schoolId })
      .in('family_id', familyIds)
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

export async function getStudentOutstandingBalance(schoolId, studentId) {
  const items = await getUnpaidItems(schoolId);
  return items
    .filter(i => i.student_id === studentId)
    .reduce((sum, i) => sum + Number(i.remaining_balance || 0), 0);
}

export async function getPeriodItems(schoolId, month, year) {
  const PAGE = 1000;
  // Fire all 3 pages in parallel — we know the school has ~2400 rows so
  // always 3 pages. If any page returns fewer than PAGE rows it's the last.
  const fetcher = (from) => supabase
    .rpc('get_period_items', { p_school_id: schoolId, p_month: month, p_year: year })
    .select('id,student_id,family_id,challan_id,description,item_type,month,year,net_amount,paid_amount,remaining_balance,challan_status,is_current_period,voided,waiver_amount,discount_amount')
    .range(from, from + PAGE - 1)
    .then(({ data, error }) => { if (error) throw error; return data || []; });

  const [p0, p1, p2] = await Promise.all([fetcher(0), fetcher(1000), fetcher(2000)]);
  return [...p0, ...p1, ...p2];
}

export async function getActivePeriod(schoolId) {
  const { data, error } = await supabase.rpc('get_active_period', { p_school_id: schoolId });
  if (error) throw error;
  if (data && data.length) return { month: data[0].period_month, year: data[0].period_year };
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

export async function getActiveChallanForFamily(schoolId, familyId, month, year) {
  const { data, error } = await supabase
    .from('challans')
    .select('id, net_payable, total_amount, status')
    .eq('school_id', schoolId).eq('family_id', familyId)
    .eq('month', month).eq('year', year)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function ensureChallanForFamily(schoolId, familyId, month, year, userId) {
  const { data: existing, error: findErr } = await supabase
    .from('challans')
    .select('id, net_payable, total_amount')
    .eq('school_id', schoolId).eq('family_id', familyId)
    .eq('month', month).eq('year', year)
    .maybeSingle();
  if (findErr) throw findErr;
  if (existing) return existing;

  const { count } = await supabase.from('challans')
    .select('id', { count: 'exact', head: true })
    .eq('school_id', schoolId).eq('month', month).eq('year', year);
  const seq = (count || 0) + 1;
  const M = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const challanNo = `CHN${M[month - 1]}${String(year).slice(-2)}-${String(seq).padStart(4,'0')}`;

  const { data: created, error: createErr } = await supabase.from('challans').insert({
    school_id: schoolId, challan_no: challanNo, family_id: familyId,
    month, year, months_count: 1, status: 'published',
    generated_by: userId, total_amount: 0, paid_amount: 0, net_payable: 0,
    arrears_amount: 0, discount_amount: 0, waiver_amount: 0, fine_amount: 0,
  }).select('id, net_payable, total_amount').single();
  if (createErr) throw createErr;
  return created;
}

export async function attachSaleToChallan(schoolId, challanId, studentId, saleId, description, creditAmount, month, year) {
  const { data: item, error } = await supabase.from('challan_items').insert({
    challan_id: challanId, school_id: schoolId, student_id: studentId,
    sale_id: saleId, fee_head_id: null, description,
    amount: creditAmount, net_amount: creditAmount,
    discount_amount: 0, waiver_amount: 0,
    item_type: 'pos', month, year,
  }).select().single();
  if (error) throw error;

  const { data: c } = await supabase.from('challans')
    .select('net_payable, total_amount').eq('id', challanId).single();
  await supabase.from('challans').update({
    net_payable: Number(c?.net_payable || 0) + Number(creditAmount),
    total_amount: Number(c?.total_amount || 0) + Number(creditAmount),
  }).eq('id', challanId);

  return item;
}

// ─── SCHOOL LOGO ─────────────────────────────────────────
export async function uploadSchoolLogo(schoolId, file) {
  const ext = file.name.split('.').pop();
  const path = `${schoolId}/logo.${ext}`;
  const { error } = await supabase.storage.from('school-assets').upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('school-assets').getPublicUrl(path);
  return data.publicUrl;
}

export async function wipeSchoolData(categories) {
  const { data, error } = await supabase.rpc('wipe_school_data', { p_categories: categories });
  if (error) throw error;
  return data;
}

export async function getAdvanceBalances(schoolId) {
  const { data, error } = await supabase
    .from('advance_ledger')
    .select('student_id, balance_after, created_at')
    .eq('school_id', schoolId)
    .order('student_id', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  const map = {};
  for (const row of data || []) {
    if (!(row.student_id in map)) map[row.student_id] = Number(row.balance_after || 0);
  }
  return map;
}

// ─── PAYMENTS ────────────────────────────────────────────
export async function savePayment(payment, items) {
  const { data, error } = await supabase.from('payments').insert(payment).select().single();
  if (error) throw error;
  if (items && items.length > 0) {
    const { error: itemError } = await supabase.from('payment_items').insert(
      items.map(i => ({ ...i, payment_id: data.id, school_id: payment.school_id }))
    );
    if (itemError) throw itemError;
  }
  return data;
}

export async function getPayments(schoolId, familyId) {
  const { data, error } = await supabase
    .from('payments')
    .select('*, payment_items(*)')
    .eq('school_id', schoolId)
    .eq('family_id', familyId)
    .order('payment_date', { ascending: false });
  if (error) throw error;
  return data;
}

// ─── ATTENDANCE ──────────────────────────────────────────
export async function getAttendance(schoolId, sectionId, date) {
  const { data, error } = await supabase
    .from('attendance')
    .select('*, students(name,roll_no,admission_no)')
    .eq('school_id', schoolId)
    .eq('section_id', sectionId)
    .eq('date', date);
  if (error) throw error;
  return data;
}

export async function saveAttendance(records) {
  const { error } = await supabase.from('attendance').upsert(records, { onConflict: 'student_id,date' });
  if (error) throw error;
}

export async function getStudentAttendance(studentId, month, year) {
  const startDate = `${year}-${String(month).padStart(2,'0')}-01`;
  const endDate = `${year}-${String(month).padStart(2,'0')}-31`;
  const { data, error } = await supabase
    .from('attendance')
    .select('*')
    .eq('student_id', studentId)
    .gte('date', startDate)
    .lte('date', endDate);
  if (error) throw error;
  return data;
}

// School-wide, today only. Returns marked counts by status + total marked,
// so the dashboard can distinguish "0% attendance" from "not marked yet".
// late/leave are never folded into present — kept as separate ground-truth buckets.
export async function getTodayAttendanceSummary(schoolId, date) {
  const { data, error } = await supabase
    .from('attendance')
    .select('status')
    .eq('school_id', schoolId)
    .eq('date', date);
  if (error) throw error;
  const counts = { present: 0, absent: 0, late: 0, leave: 0 };
  (data || []).forEach(r => { if (counts.hasOwnProperty(r.status)) counts[r.status]++; });
  return { ...counts, markedCount: (data || []).length };
}

// Total owed across active vendors — Finance/Payables card.
export async function getPayablesSummary(schoolId) {
  const { data, error } = await supabase
    .from('vendors')
    .select('current_balance')
    .eq('school_id', schoolId)
    .eq('is_active', true);
  if (error) throw error;
  const total = (data || []).reduce((sum, v) => sum + Number(v.current_balance || 0), 0);
  return { total, vendorCount: (data || []).length };
}

// Open (non-resolved) complaint count. Only 'resolved' is a confirmed live status —
// anything else counts as open rather than guessing at a closed/dismissed vocabulary.
export async function getComplaintsSummary(schoolId) {
  const { data, error } = await supabase
    .from('complaints')
    .select('status')
    .eq('school_id', schoolId);
  if (error) throw error;
  const open = (data || []).filter(c => c.status !== 'resolved').length;
  return { open, total: (data || []).length };
}

// Staff attendance uses 'Present'/'Absent' (capitalized) — a DIFFERENT convention
// from student attendance's lowercase values. Do not copy the student status keys here.
export async function getTodayStaffAttendanceSummary(schoolId, date) {
  const [{ data: attData, error: attErr }, { data: staffData, error: staffErr }] = await Promise.all([
    supabase.from('staff_attendance').select('status').eq('school_id', schoolId).eq('date', date),
    supabase.from('staff').select('id').eq('school_id', schoolId).eq('status', 'Active'),
  ]);
  if (attErr) throw attErr;
  if (staffErr) throw staffErr;
  const counts = { Present: 0, Absent: 0, Late: 0, Leave: 0 };
  (attData || []).forEach(r => { if (counts.hasOwnProperty(r.status)) counts[r.status]++; });
  return { ...counts, markedCount: (attData || []).length, totalStaff: (staffData || []).length };
}

// Exam ops summary (open exams, papers not submitted, marks pending) — multi-table
// join/aggregate too complex for plain PostgREST chaining, so it's a Postgres RPC.
export async function getExamOpsSummary(schoolId) {
  const { data, error } = await supabase.rpc('get_dashboard_exam_ops_summary', { p_school_id: schoolId });
  if (error) throw error;
  return data;
}

export async function getStudentAttendanceHistory(studentId) {
  const from = new Date();
  from.setMonth(from.getMonth() - 11);
  const fromDate = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-01`;
  const { data, error } = await supabase
    .from('attendance')
    .select('date, status')
    .eq('student_id', studentId)
    .gte('date', fromDate)
    .order('date', { ascending: true });
  if (error) throw error;
  return data;
}

export async function getStaffAttendance(schoolId, date) {
  const { data, error } = await supabase
    .from('staff_attendance')
    .select('staff_id, status')
    .eq('school_id', schoolId)
    .eq('date', date);
  if (error) throw error;
  const map = {};
  (data || []).forEach(r => { map[r.staff_id] = r.status; });
  return map;
}

export async function saveStaffAttendance(schoolId, date, records, markedBy) {
  const rows = Object.entries(records).map(([staff_id, status]) => ({
    school_id: schoolId, staff_id, date, status, marked_by: markedBy,
  }));
  if (rows.length === 0) return;
  const { error } = await supabase
    .from('staff_attendance')
    .upsert(rows, { onConflict: 'staff_id,date' });
  if (error) throw error;
}

export async function getStaffAdvances(schoolId) {
  const { data, error } = await supabase
    .from('staff_advances')
    .select('*')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(r => ({ ...r, kind: 'advance' }));
}

export async function getStaffLoans(schoolId) {
  const { data, error } = await supabase
    .from('staff_loans')
    .select('*')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(r => ({ ...r, kind: 'loan' }));
}

export async function createStaffAdvance(schoolId, { staffId, amount, date, reason, recoveryMonths, monthlyRecovery }, approvedBy) {
  const { error } = await supabase.from('staff_advances').insert({
    school_id: schoolId, staff_id: staffId, amount, advance_date: date, reason,
    recovery_months: recoveryMonths || null, monthly_recovery: monthlyRecovery || 0,
    total_recovered: 0, balance: amount, status: 'active', approved_by: approvedBy,
  });
  if (error) throw error;
}

export async function createStaffLoan(schoolId, { staffId, amount, date, reason, recoveryMonths, monthlyRecovery, interestRate }, approvedBy) {
  const { error } = await supabase.from('staff_loans').insert({
    school_id: schoolId, staff_id: staffId, loan_amount: amount, loan_date: date, reason,
    recovery_months: recoveryMonths || null, monthly_recovery: monthlyRecovery || 0,
    total_recovered: 0, balance: amount, interest_rate: interestRate || 0, status: 'active', approved_by: approvedBy,
  });
  if (error) throw error;
}

export async function clearStaffAdvance(id) {
  const { data, error } = await supabase.from('staff_advances').select('amount').eq('id', id).single();
  if (error) throw error;
  const { error: upErr } = await supabase.from('staff_advances').update({ status: 'cleared', total_recovered: data.amount, balance: 0 }).eq('id', id);
  if (upErr) throw upErr;
}

export async function clearStaffLoan(id) {
  const { data, error } = await supabase.from('staff_loans').select('loan_amount').eq('id', id).single();
  if (error) throw error;
  const { error: upErr } = await supabase.from('staff_loans').update({ status: 'cleared', total_recovered: data.loan_amount, balance: 0 }).eq('id', id);
  if (upErr) throw upErr;
}

export async function getVendors(schoolId) {
  const { data, error } = await supabase.from('vendors').select('*').eq('school_id', schoolId).eq('is_active', true).order('name');
  if (error) throw error;
  return (data || []).map(v => ({
    id: v.id, vendorNo: v.vendor_no || '', name: v.name, contact: v.contact_person, phone: v.phone,
    email: v.email || '', address: v.address || '',
    balance: Number(v.current_balance || 0), openingBalance: Number(v.opening_balance || 0), openingDate: v.opening_balance_date || '',
    defaultDiscountPercent: v.default_discount_percent == null ? '' : Number(v.default_discount_percent),
  }));
}

// Books the vendor's opening balance (what the school already owed before using the system); only the difference is posted
export async function setVendorOpeningBalance(vendorId, amount, asOf) {
  const { error } = await supabase.rpc('set_vendor_opening_balance', { p_vendor_id: vendorId, p_amount: Number(amount || 0), p_as_of: asOf || null });
  if (error) throw error;
}

export async function saveVendor(schoolId, vendor) {
  const row = { school_id: schoolId, name: vendor.name, contact_person: vendor.contact, phone: vendor.phone, email: vendor.email || '', address: vendor.address || '',
    default_discount_percent: vendor.defaultDiscountPercent === '' || vendor.defaultDiscountPercent == null ? null : Number(vendor.defaultDiscountPercent) };
  if (vendor.id) {
    const { error } = await supabase.from('vendors').update(row).eq('id', vendor.id);
    if (error) throw error;
    return vendor.id;
  }
  const { data, error } = await supabase.from('vendors').insert({ ...row, opening_balance: 0, current_balance: 0 }).select('id').single();
  if (error) throw error;
  return data.id;
}

export async function saveVendorPayment(schoolId, vendorId, { amount, paymentMethod, paymentDate, referenceNo, notes }, userId) {
  const { data, error } = await supabase.from('vendor_payments').insert({
    school_id:      schoolId,
    vendor_id:      vendorId,
    amount,
    payment_method: paymentMethod || 'Cash',
    payment_date:   paymentDate || todayISO(),
    reference_no:   referenceNo || null,
    notes:          notes || null,
    created_by:     userId || null,
  }).select('id').single();
  if (error) throw error;
  return data;
}

export async function getVendorLedger(vendorId) {
  const [purchases, payments, adjustments, vendorRow] = await Promise.all([
    supabase.from('purchases')
      .select('id, purchase_date, total_amount, delivery_method, is_reversed, created_at')
      .eq('vendor_id', vendorId).order('created_at', { ascending: false }),
    supabase.from('vendor_payments')
      .select('id, amount, payment_date, payment_method, reference_no, notes, created_at')
      .eq('vendor_id', vendorId).order('created_at', { ascending: false }),
    supabase.from('purchase_adjustments')
      .select('id, value_delta, reason, created_at, adjustment_no')
      .eq('vendor_id', vendorId).order('created_at', { ascending: false }),
    supabase.from('vendors').select('opening_balance, opening_balance_date, created_at').eq('id', vendorId).single(),
  ]);

  const ob = Number(vendorRow.data?.opening_balance || 0);
  const rows = [
    ...(ob !== 0 ? [{
      type: 'opening', date: vendorRow.data.opening_balance_date || vendorRow.data.created_at?.split('T')[0],
      timestamp: (vendorRow.data.opening_balance_date || '1970-01-01') + 'T00:00:00',
      description: 'Opening balance', debit: ob < 0 ? -ob : 0, credit: ob > 0 ? ob : 0, reversed: false,
    }] : []),
    ...(purchases.data || []).map(p => ({
      type: 'purchase', date: p.purchase_date, timestamp: p.created_at,
      description: `GRN — ${p.delivery_method === 'self_pickup' ? 'self pickup' : 'vendor delivered'}${p.is_reversed ? ' (reversed)' : ''}`,
      debit: 0, credit: p.is_reversed ? 0 : p.total_amount,
      reversed: !!p.is_reversed,
    })),
    ...(payments.data || []).map(p => ({
      type: 'payment', date: p.payment_date, timestamp: p.created_at,
      description: `Payment — ${p.payment_method || 'Cash'}${p.reference_no ? ` (${p.reference_no})` : ''}`,
      debit: p.amount, credit: 0, reversed: false,
    })),
    ...(adjustments.data || []).map(a => ({
      type: 'adjustment', date: a.created_at?.split('T')[0], timestamp: a.created_at,
      description: `${a.adjustment_no || 'ADJ'} — ${a.reason || ''}`,
      debit: a.value_delta < 0 ? Math.abs(a.value_delta) : 0,
      credit: a.value_delta > 0 ? a.value_delta : 0,
      reversed: false,
    })),
  ];

  // Compute running balance chronologically, then reverse for newest-first display
  const chronological = [...rows].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let bal = 0;
  const balanced = chronological.map(r => {
    bal += r.credit - r.debit;
    return { ...r, runningBalance: bal };
  });
  return balanced.reverse();
}

export async function getPurchases(schoolId) {
  const { data, error } = await supabase
    .from('purchases')
    .select('*, vendors(name), purchase_items(*, products(name))')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function savePurchase(schoolId, vendorId, purchaseDate, invoiceNo, lineItems, userId) {
  const totalAmount = lineItems.reduce((a, i) => a + i.quantity * i.unitCost, 0);
  const { data: purchase, error: pErr } = await supabase.from('purchases')
    .insert({ school_id: schoolId, vendor_id: vendorId, purchase_date: purchaseDate,
      invoice_no: invoiceNo || null, total_amount: totalAmount, created_by: userId || null })
    .select().single();
  if (pErr) throw pErr;

  // purchase_items uses unit_price/total_price (no school_id column — RLS via purchases join)
  const { error: itemErr } = await supabase.from('purchase_items').insert(
    lineItems.map(i => ({
      purchase_id: purchase.id, product_id: i.productId,
      quantity: i.quantity, unit_price: i.unitCost, total_price: i.quantity * i.unitCost,
    }))
  );
  if (itemErr) throw itemErr;

  // FIFO lots — one per line item; consume_stock_fifo drains these oldest-first
  const { error: lotErr } = await supabase.from('product_stock_lots').insert(
    lineItems.map(i => ({
      school_id: schoolId, product_id: i.productId, purchase_id: purchase.id,
      quantity_received: i.quantity, quantity_remaining: i.quantity, unit_cost: i.unitCost,
    }))
  );
  if (lotErr) throw lotErr;

  return purchase;
}

export async function receiveGRN(schoolId, vendorId, poId, purchaseDate, lineItems, deliveryMethod, freightOrTransportCost, userId, invoiceNo, notes) {
  const { data, error } = await supabase.rpc('receive_grn', {
    p_school_id:          schoolId,
    p_vendor_id:          vendorId,
    p_po_id:              poId || null,
    p_purchase_date:      purchaseDate,
    p_line_items:         lineItems,
    p_delivery_method:    deliveryMethod,
    p_freight_charges:    deliveryMethod === 'vendor_delivered' ? (freightOrTransportCost || 0) : 0,
    p_own_transport_cost: deliveryMethod === 'self_pickup'      ? (freightOrTransportCost || 0) : 0,
    p_user_id:            userId || null,
    p_invoice_no:         invoiceNo || null,
    p_notes:              notes || null,
  });
  if (error) throw error;
  return data;
}

// Latest vendor price of every product, from the most recent GRN: { [productId]: { price, date } }
export async function getLastPurchasePrices() {
  const out = {};
  const { data, error } = await supabase.rpc('last_purchase_prices');
  if (!error) {
    (data || []).forEach(r => { out[r.product_id] = { price: Number(r.unit_price), date: r.purchase_date }; });
    return out;
  }
  // the lookup function is not installed yet: read the lines directly, newest GRN first
  const { data: rows, error: e2 } = await supabase.from('purchase_items')
    .select('product_id, unit_price, quantity, purchases!inner(purchase_date, created_at, is_reversed)')
    .eq('purchases.is_reversed', false).order('created_at', { ascending: false, referencedTable: 'purchases' }).limit(1000);
  if (e2) throw e2;
  (rows || []).filter(r => r.quantity > 0)
    .sort((a, b) => String(b.purchases.purchase_date).localeCompare(String(a.purchases.purchase_date)) || String(b.purchases.created_at).localeCompare(String(a.purchases.created_at)))
    .forEach(r => { if (!out[r.product_id]) out[r.product_id] = { price: Number(r.unit_price), date: r.purchases.purchase_date }; });
  return out;
}

// One GRN with its lines and the stock lot of each line, for the edit form
export async function getPurchaseForEdit(purchaseId) {
  const { data, error } = await supabase.from('purchases')
    .select('*, purchase_items(*), product_stock_lots(purchase_item_id, quantity_received, quantity_remaining, note)')
    .eq('id', purchaseId).single();
  if (error) throw error;
  return data;
}

export async function editGRN(purchaseId, purchaseDate, invoiceNo, notes, lineItems, deliveryMethod, freightOrTransportCost, reason) {
  const { data, error } = await supabase.rpc('edit_grn', {
    p_purchase_id:        purchaseId,
    p_purchase_date:      purchaseDate,
    p_invoice_no:         invoiceNo || null,
    p_notes:              notes || null,
    p_line_items:         lineItems,
    p_delivery_method:    deliveryMethod,
    p_freight_charges:    deliveryMethod === 'vendor_delivered' ? (freightOrTransportCost || 0) : 0,
    p_own_transport_cost: deliveryMethod === 'self_pickup'      ? (freightOrTransportCost || 0) : 0,
    p_reason:             reason,
  });
  if (error) throw error;
  return data;
}

export async function getPurchaseOrders(schoolId) {
  const { data, error } = await supabase
    .from('purchase_orders')
    .select('*, vendors(name), purchase_order_items(*, products(name))')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function savePurchaseOrder(schoolId, vendorId, orderDate, expectedDate, lineItems, notes, userId) {
  const { count } = await supabase
    .from('purchase_orders')
    .select('*', { count: 'exact', head: true })
    .eq('school_id', schoolId);
  const poNo = `PO-${String((count || 0) + 1).padStart(4, '0')}`;

  const { data: po, error: poErr } = await supabase
    .from('purchase_orders')
    .insert({ school_id: schoolId, vendor_id: vendorId, po_no: poNo,
      order_date: orderDate, expected_date: expectedDate || null,
      notes: notes || null, created_by: userId || null })
    .select().single();
  if (poErr) throw poErr;

  if (lineItems.length > 0) {
    const { error: itemErr } = await supabase
      .from('purchase_order_items')
      .insert(lineItems.map(i => ({
        po_id: po.id, school_id: schoolId,
        product_id: i.productId, quantity_ordered: i.quantity,
        expected_unit_cost: i.expectedCost || null,
        discount_type: i.discountValue > 0 ? i.discountType : null,
        discount_value: i.discountValue > 0 ? i.discountValue : null,
      })));
    if (itemErr) throw itemErr;
  }
  return po;
}

export async function cancelPurchaseOrder(poId) {
  const { error } = await supabase
    .from('purchase_orders')
    .update({ status: 'cancelled' })
    .in('status', ['draft', 'sent'])
    .eq('id', poId);
  if (error) throw error;
}

export async function reversePurchase(purchaseId, reason, userId) {
  const { data: purchase, error } = await supabase
    .from('purchases').select('total_amount, is_reversed').eq('id', purchaseId).single();
  if (error) throw error;
  if (purchase.is_reversed) throw new Error('Purchase already reversed');

  const { error: revErr } = await supabase.from('purchases').update({
    is_reversed: true, reversed_by: userId || null,
    reversed_at: new Date().toISOString(), reversal_reason: reason || null,
  }).eq('id', purchaseId);
  if (revErr) throw revErr;

  // Zero remaining FIFO lots (stock sold from this lot stays gone — only unreceived units reversed)
  await supabase.from('product_stock_lots')
    .update({ quantity_remaining: 0 })
    .eq('purchase_id', purchaseId).gt('quantity_remaining', 0);

  const { error: balErr } = await supabase.rpc('reverse_purchase_vendor_balance', {
    p_purchase_id: purchaseId, p_amount: purchase.total_amount,
  });
  if (balErr) throw balErr;

  return purchase;
}

export async function getPurchaseLots(purchaseId) {
  const { data, error } = await supabase
    .from('product_stock_lots')
    .select('*, products(name)')
    .eq('purchase_id', purchaseId)
    .order('received_at');
  if (error) throw error;
  return data || [];
}

export async function applyPurchaseAdjustment(lotId, newQty, newCost, reason, userId) {
  const { data, error } = await supabase.rpc('apply_purchase_adjustment', {
    p_lot_id: lotId, p_new_quantity: newQty, p_new_unit_cost: newCost,
    p_reason: reason, p_user_id: userId || null,
  });
  if (error) throw error;
  return data;
}

export async function getPurchaseAdjustment(id) {
  const { data, error } = await supabase
    .from('purchase_adjustments')
    .select('*, products(name), vendors(name), purchases(purchase_date)')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function getAssets(schoolId) {
  const { data, error } = await supabase.from('assets').select('*').eq('school_id', schoolId).order('name');
  if (error) throw error;
  return (data || []).map(a => ({
    id: a.id, name: a.name, category: a.category, quantity: Number(a.quantity || 1),
    purchaseDate: a.purchase_date, purchasePrice: Number(a.purchase_price || 0),
    usefulLife: Number(a.useful_life_years || 0), currentValue: Number(a.current_value || 0),
    location: a.location || '', condition: a.condition || 'Good',
    vendorId: a.vendor_id || '', notes: a.notes || '',
  }));
}

export async function saveAsset(schoolId, asset, userId) {
  const row = {
    school_id: schoolId, name: asset.name, category: asset.category,
    quantity: Number(asset.quantity || 1),
    purchase_date: asset.purchaseDate || null,
    purchase_price: Number(asset.purchasePrice || 0),
    useful_life_years: Number(asset.usefulLife || 0) || null,
    current_value: Number(asset.currentValue || asset.purchasePrice || 0),
    location: asset.location || null, condition: asset.condition || 'Good',
    notes: asset.notes || null, created_by: userId || null,
  };
  if (asset.id) {
    const { error } = await supabase.from('assets').update(row).eq('id', asset.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('assets').insert(row);
    if (error) throw error;
  }
}

export async function deleteAsset(assetId) {
  const { error } = await supabase.from('assets').delete().eq('id', assetId);
  if (error) throw error;
}

// ─── SALES ───────────────────────────────────────────────
export async function saveSale(sale, items) {
  const { data, error } = await supabase.from('sales').insert(sale).select().single();
  if (error) throw error;
  if (items && items.length > 0) {
    const { error: itemError } = await supabase.from('sale_items').insert(
      items.map(i => ({ ...i, sale_id: data.id }))
    );
    if (itemError) throw itemError;
    // Phase 3: call consume_stock_fifo per item for FIFO COGS
  }
  return data;
}

export async function getSales(schoolId, activeStudentIds) {
  let query = supabase
    .from('sales')
    .select('*, students(name, families(father_name, family_id_display)), sale_items(*), users:sold_by(name)')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (activeStudentIds && activeStudentIds.length > 0) {
    query = query.in('student_id', activeStudentIds);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// POS Sales tab: the latest 50 sales, or, when a search or date filter is given, the matching ones (up to 300, newest first).
const SALE_SELECT = '*, students(name, classes(name), sections(name), families(father_name, family_id_display)), sale_items(*), users:sold_by(name)';
// Card totals for one fee month (latest fee period when month/year are not given), net of returns
export async function getSalesSummary(schoolId, month = null, year = null) {
  const { data, error } = await supabase.rpc('pos_sales_summary', { p_school_id: schoolId, p_month: month, p_year: year });
  if (error) throw error;
  return data;
}
export async function getSalesPage(schoolId, { search = '', from = '', to = '', method = '' } = {}) {
  const filtered = !!(search.trim() || from || to || method);
  if (!filtered) {
    const { data, error } = await supabase.from('sales').select(SALE_SELECT)
      .eq('school_id', schoolId).order('created_at', { ascending: false }).limit(50);
    if (error) throw error;
    return { rows: data || [], filtered: false, capped: false };
  }
  const { data: ids, error: idErr } = await supabase.rpc('pos_sales_find', {
    p_school_id: schoolId, p_q: search.trim() || null, p_from: from || null, p_to: to || null, p_method: method || null, p_limit: 300,
  });
  if (idErr) throw idErr;
  const list = (ids || []).map(x => (typeof x === 'string' ? x : Object.values(x)[0]));
  const chunks = [];
  for (let i = 0; i < list.length; i += 80) chunks.push(list.slice(i, i + 80));
  const parts = await Promise.all(chunks.map(async (c) => {
    const { data, error } = await supabase.from('sales').select(SALE_SELECT).in('id', c);
    if (error) throw error;
    return data || [];
  }));
  const rows = parts.flat().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  return { rows, filtered: true, capped: list.length >= 300 };
}

// ─── STAFF ───────────────────────────────────────────────
export async function getStaff(schoolId) {
  const [{ data: staffData, error }, { data: salaryData }] = await Promise.all([
    supabase.from('staff').select('*, designations(name, department)').eq('school_id', schoolId).order('name'),
    supabase.from('salary_structures').select('staff_id, basic_salary').eq('is_active', true),
  ]);
  if (error) throw error;
  const salaryMap = {};
  for (const s of (salaryData || [])) salaryMap[s.staff_id] = Number(s.basic_salary || 0);
  return (staffData || []).map(s => ({
    id: s.id, employeeNo: s.employee_no, name: s.name,
    department: s.designations?.department || s.department,
    designation: s.designations?.name || '', designationId: s.designation_id,
    gender: s.gender, dob: s.dob, joiningDate: s.joining_date, joinDate: s.joining_date,
    leavingDate: s.leaving_date, cnic: s.cnic, phone: s.phone,
    whatsapp: s.whatsapp, email: s.email, address: s.address,
    qualification: s.qualification, bloodGroup: s.blood_group,
    emergencyContact: s.emergency_contact, experienceYears: s.experience_years,
    bankName: s.bank_name, bankAccount: s.bank_account, eobiNo: s.eobi_no,
    status: s.status, salary: salaryMap[s.id] || 0, photoUrl: s.photo_url, notes: s.notes,
    user_id: s.user_id,
  }));
}

export async function saveStaff(schoolId, staffMember) {
  const row = {
    school_id: schoolId, employee_no: staffMember.employeeNo, name: staffMember.name,
    department: staffMember.department, designation_id: staffMember.designationId || null,
    gender: staffMember.gender, dob: staffMember.dob || null,
    joining_date: staffMember.joiningDate || null, leaving_date: staffMember.leavingDate || null,
    cnic: staffMember.cnic, phone: staffMember.phone, whatsapp: staffMember.whatsapp,
    email: staffMember.email, address: staffMember.address,
    qualification: staffMember.qualification, blood_group: staffMember.bloodGroup,
    emergency_contact: staffMember.emergencyContact,
    experience_years: staffMember.experienceYears ? Number(staffMember.experienceYears) : null,
    bank_name: staffMember.bankName, bank_account: staffMember.bankAccount,
    eobi_no: staffMember.eobiNo, status: staffMember.status || 'Active', notes: staffMember.notes,
  };
  let staffId = staffMember.id;
  if (staffId) {
    const { error } = await supabase.from('staff').update(row).eq('id', staffId);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from('staff').insert(row).select('id').single();
    if (error) throw error;
    staffId = data.id;
  }
  if (staffMember.salary !== undefined && staffMember.salary !== '') {
    const salary = Number(staffMember.salary || 0);
    await supabase.from('salary_structures').update({ is_active: false }).eq('staff_id', staffId);
    const { error: salErr } = await supabase.from('salary_structures').insert({
      school_id: schoolId, staff_id: staffId, basic_salary: salary, is_active: true,
      effective_from: todayISO(),
    });
    if (salErr) throw salErr;
  }
  return staffId;
}

export async function deleteStaff(id) {
  const { error: acErr } = await supabase.from('academy_staff').delete().eq('staff_id', id);
  if (acErr) throw acErr;
  const { error } = await supabase.from('staff').delete().eq('id', id);
  if (error) throw error;
}

export async function getDesignations() {
  const { data, error } = await supabase.from('designations').select('id, name, department').order('name');
  if (error) throw error;
  return data || [];
}

export async function getSubjects(schoolId) {
  const { data, error } = await supabase.from('subjects').select('id, name, code').eq('school_id', schoolId).eq('is_active', true).order('order_num');
  if (error) throw error;
  return data || [];
}

export async function getTeacherAssignments(schoolId) {
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) return [];
  const { data, error } = await supabase.from('teacher_assignments')
    .select('*, staff(name), subjects(id, name), sections(id, name, class_id, classes(name))')
    .eq('school_id', schoolId).eq('academic_year_id', yearId);
  if (error) throw error;
  return data || [];
}

export async function getCurrentAcademicYear(schoolId) {
  const { data, error } = await supabase.from('academic_years')
    .select('id').eq('school_id', schoolId).eq('is_current', true).maybeSingle();
  if (error) throw error;
  return data?.id || null;
}

export async function assignTeacherSubject(schoolId, teacherId, subjectId, sectionId, isClassTeacher, academicYearId) {
  const { error } = await supabase.from('teacher_assignments').insert({
    school_id: schoolId, teacher_id: teacherId, subject_id: subjectId,
    section_id: sectionId, is_class_teacher: isClassTeacher, academic_year_id: academicYearId,
  });
  if (error) throw error;
}

export async function removeTeacherAssignment(id) {
  const { error } = await supabase.from('teacher_assignments').delete().eq('id', id);
  if (error) throw error;
}

// Class incharge is stored as is_class_teacher on the teacher's rows in that section.
// Setting it clears every other teacher's flag in the same section.
export async function setClassIncharge(schoolId, sectionId, teacherId) {
  // Without the year filter this clears the incharge in every session, not just
  // the current one.
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) throw new Error('No current academic session is set');
  const { error: e1 } = await supabase.from('teacher_assignments')
    .update({ is_class_teacher: false })
    .eq('school_id', schoolId).eq('section_id', sectionId).eq('academic_year_id', yearId);
  if (e1) throw e1;
  const { error: e2 } = await supabase.from('teacher_assignments')
    .update({ is_class_teacher: true })
    .eq('school_id', schoolId).eq('section_id', sectionId).eq('teacher_id', teacherId)
    .eq('academic_year_id', yearId);
  if (e2) throw e2;
}

export async function clearClassIncharge(schoolId, sectionId, teacherId) {
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) throw new Error('No current academic session is set');
  const { error } = await supabase.from('teacher_assignments')
    .update({ is_class_teacher: false })
    .eq('school_id', schoolId).eq('section_id', sectionId).eq('teacher_id', teacherId)
    .eq('academic_year_id', yearId);
  if (error) throw error;
}

// Non-teaching staff (drivers, guards, ...) are flagged is_teacher = false and hidden from assignments.
// Needs: alter table staff add column is_teacher boolean not null default true;
export async function getNonTeacherIds(schoolId) {
  const { data, error } = await supabase.from('staff').select('id').eq('school_id', schoolId).eq('is_teacher', false);
  if (error) throw error;
  return (data || []).map(r => r.id);
}

export async function setStaffIsTeacher(id, isTeacher) {
  const { error } = await supabase.from('staff').update({ is_teacher: isTeacher }).eq('id', id);
  if (error) throw error;
}

export async function getSalaryStructure(staffId) {
  const { data, error } = await supabase
    .from('salary_structures')
    .select('*')
    .eq('staff_id', staffId)
    .eq('is_active', true)
    .single();
  if (error) return null;
  return data;
}

export async function saveSalaryStructure(structure) {
  await supabase.from('salary_structures').update({ is_active: false }).eq('staff_id', structure.staff_id);
  const { data, error } = await supabase.from('salary_structures').insert({ ...structure, is_active: true }).select().single();
  if (error) throw error;
  return data;
}

// ─── PAYROLL ─────────────────────────────────────────────
export async function getPayroll(schoolId, month, year) {
  const { data, error } = await supabase
    .from('payroll')
    .select('*, payroll_items(*, staff(name,employee_no)), academy_payroll_items(*)')
    .eq('school_id', schoolId)
    .eq('month', month)
    .eq('year', year)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createPayrollPeriod(schoolId, month, year, generatedBy) {
  const { data, error } = await supabase
    .from('payroll')
    .insert({ school_id: schoolId, month, year, status: 'draft', generated_by: generatedBy })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function addPayrollItems(payrollId, items) {
  if (!items || items.length === 0) return;
  const { error } = await supabase.from('payroll_items').insert(
    items.map(i => ({ ...i, payroll_id: payrollId }))
  );
  if (error) throw error;
}

export async function markPayrollItemPaid(itemId, paymentMethod) {
  const { error } = await supabase
    .from('payroll_items')
    .update({ payment_date: todayISO(), payment_method: paymentMethod || 'cash' })
    .eq('id', itemId);
  if (error) throw error;
}

async function recoverStaffDebt(table, dateCol, staffId, amount) {
  if (!amount || amount <= 0) return;
  const { data, error } = await supabase
    .from(table)
    .select('id, balance, total_recovered')
    .eq('staff_id', staffId)
    .neq('status', 'cleared')
    .order(dateCol, { ascending: true });
  if (error) throw error;
  let remaining = amount;
  for (const row of (data || [])) {
    if (remaining <= 0) break;
    const bal = Number(row.balance || 0);
    const applied = Math.min(bal, remaining);
    if (applied <= 0) continue;
    const newBalance = bal - applied;
    const newRecovered = Number(row.total_recovered || 0) + applied;
    const { error: upErr } = await supabase
      .from(table)
      .update({ balance: newBalance, total_recovered: newRecovered, status: newBalance <= 0 ? 'cleared' : 'active' })
      .eq('id', row.id);
    if (upErr) throw upErr;
    remaining -= applied;
  }
}

export async function updatePayrollTotals(payrollId) {
  const [schoolRes, academyRes] = await Promise.all([
    supabase.from('payroll_items').select('gross_salary, total_deductions, net_salary').eq('payroll_id', payrollId),
    supabase.from('academy_payroll_items').select('gross_salary, total_deductions, net_salary').eq('payroll_id', payrollId),
  ]);
  if (schoolRes.error) throw schoolRes.error;
  if (academyRes.error) throw academyRes.error;
  const data = [...(schoolRes.data || []), ...(academyRes.data || [])];
  const totals = (data || []).reduce((acc, r) => ({
    total_gross: acc.total_gross + Number(r.gross_salary || 0),
    total_deductions: acc.total_deductions + Number(r.total_deductions || 0),
    total_net: acc.total_net + Number(r.net_salary || 0),
  }), { total_gross: 0, total_deductions: 0, total_net: 0 });
  const { error: upErr } = await supabase.from('payroll').update(totals).eq('id', payrollId);
  if (upErr) throw upErr;
}

export async function postPayrollItemJournal(itemId, userId) {
  const { data, error } = await supabase.rpc('post_payroll_item_journal', {
    p_item_id: itemId, p_user_id: userId,
  });
  if (error) throw error;
  return data;
}

export async function refreshPayrollStatus(payrollId) {
  const [schoolRes, academyRes] = await Promise.all([
    supabase.from('payroll_items').select('payment_date').eq('payroll_id', payrollId),
    supabase.from('academy_payroll_items').select('payment_date').eq('payroll_id', payrollId),
  ]);
  if (schoolRes.error) throw schoolRes.error;
  if (academyRes.error) throw academyRes.error;
  const data = [...(schoolRes.data || []), ...(academyRes.data || [])];
  const allPaid = data.length > 0 && data.every(r => r.payment_date);
  const { error: upErr } = await supabase
    .from('payroll')
    .update({ status: allPaid ? 'paid' : 'draft', paid_at: allPaid ? new Date().toISOString() : null })
    .eq('id', payrollId);
  if (upErr) throw upErr;
}

export async function payPayrollItem(item, paymentMethod, userId) {
  await markPayrollItemPaid(item.id, paymentMethod);
  try {
    await postPayrollItemJournal(item.id, userId);
  } catch (e) {
    await supabase.from('payroll_items').update({ payment_date: null }).eq('id', item.id);
    throw e;
  }
  await recoverStaffDebt('staff_advances', 'advance_date', item.staff_id, Number(item.advance_recovery || 0));
  await recoverStaffDebt('staff_loans', 'loan_date', item.staff_id, Number(item.loan_recovery || 0));
  await refreshPayrollStatus(item.payroll_id);
}

// ─── ACADEMY (second employment: own salary, dates, attendance and payroll items) ──
const mapAcademy = (r) => ({
  id: r.id, staffId: r.staff_id, monthlySalary: Number(r.monthly_salary || 0),
  startDate: r.start_date || '', endDate: r.end_date || '', status: r.status || 'Active',
});

export async function getAcademyStaff(schoolId) {
  const { data, error } = await supabase.from('academy_staff').select('*').eq('school_id', schoolId);
  if (error) throw error;
  return (data || []).map(mapAcademy);
}

export async function saveAcademyEngagement(schoolId, staffId, eng) {
  const { error } = await supabase.from('academy_staff').upsert({
    school_id: schoolId, staff_id: staffId,
    monthly_salary: Number(eng.monthlySalary || 0),
    start_date: eng.startDate || todayISO(),
    end_date: eng.endDate || null,
    status: eng.status || 'Active',
  }, { onConflict: 'staff_id' });
  if (error) throw error;
}

// Unchecking "Also works at the academy": delete the row, unless attendance or payroll
// history exists, in which case keep the row and mark it Inactive.
export async function removeAcademyEngagement(staffId) {
  const [att, pay] = await Promise.all([
    supabase.from('academy_attendance').select('id', { count: 'exact', head: true }).eq('staff_id', staffId),
    supabase.from('academy_payroll_items').select('id', { count: 'exact', head: true }).eq('staff_id', staffId),
  ]);
  if (att.error) throw att.error;
  if (pay.error) throw pay.error;
  if ((att.count || 0) + (pay.count || 0) > 0) {
    const { error } = await supabase.from('academy_staff').update({ status: 'Inactive' }).eq('staff_id', staffId);
    if (error) throw error;
    return 'deactivated';
  }
  const { error } = await supabase.from('academy_staff').delete().eq('staff_id', staffId);
  if (error) throw error;
  return 'deleted';
}

export async function getAcademyAttendance(schoolId, date) {
  const { data, error } = await supabase
    .from('academy_attendance')
    .select('staff_id, status')
    .eq('school_id', schoolId)
    .eq('date', date);
  if (error) throw error;
  const map = {};
  (data || []).forEach(r => { map[r.staff_id] = r.status; });
  return map;
}

export async function saveAcademyAttendance(schoolId, date, records, markedBy) {
  const rows = Object.entries(records).map(([staff_id, status]) => ({
    school_id: schoolId, staff_id, date, status, marked_by: markedBy,
  }));
  if (rows.length === 0) return;
  const { error } = await supabase
    .from('academy_attendance')
    .upsert(rows, { onConflict: 'staff_id,date' });
  if (error) throw error;
}

export async function addAcademyPayrollItems(payrollId, schoolId, items) {
  if (!items || items.length === 0) return;
  const { error } = await supabase.from('academy_payroll_items').insert(
    items.map(i => ({ ...i, payroll_id: payrollId, school_id: schoolId }))
  );
  if (error) throw error;
}

export async function payAcademyPayrollItem(item, paymentMethod, userId) {
  const { error } = await supabase
    .from('academy_payroll_items')
    .update({ payment_date: todayISO(), payment_method: paymentMethod || 'cash' })
    .eq('id', item.id);
  if (error) throw error;
  const { error: jErr } = await supabase.rpc('post_academy_payroll_item_journal', {
    p_item_id: item.id, p_user_id: userId,
  });
  if (jErr) {
    await supabase.from('academy_payroll_items').update({ payment_date: null }).eq('id', item.id);
    throw jErr;
  }
  await recoverStaffDebt('staff_advances', 'advance_date', item.staff_id, Number(item.advance_recovery || 0));
  await recoverStaffDebt('staff_loans', 'loan_date', item.staff_id, Number(item.loan_recovery || 0));
  await refreshPayrollStatus(item.payroll_id);
}

// One row per person: pays the school item first, then the academy item.
// Already-paid items are skipped, so a retry after a partial failure pays only what is left.
export async function payStaffMonth(schoolItem, academyItem, paymentMethod, userId) {
  if (schoolItem && !schoolItem.payment_date) {
    try { await payPayrollItem(schoolItem, paymentMethod, userId); }
    catch (e) { e.stage = 'school'; throw e; }
  }
  if (academyItem && !academyItem.payment_date) {
    try { await payAcademyPayrollItem(academyItem, paymentMethod, userId); }
    catch (e) { e.stage = 'academy'; e.schoolPaid = !!schoolItem; throw e; }
  }
}

// ─── ATTENDANCE-DRIVEN PAYROLL + LEAVE BANK ──────────────
export async function getPayrollAttendanceSummary(staffId, month, year, gross, source = 'school') {
  const { data, error } = await supabase.rpc('payroll_attendance_summary', {
    p_staff_id: staffId, p_month: month, p_year: year,
    p_source: source, p_gross: Number(gross || 0),
  });
  if (error) throw error;
  return data;
}

export async function getStaffLeaveTypes(schoolId) {
  const { data, error } = await supabase
    .from('staff_leave_types')
    .select('id, name, days_allowed, is_paid')
    .eq('school_id', schoolId)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function getStaffLeaveRequests(schoolId, status) {
  let q = supabase
    .from('staff_leave_requests')
    .select('*, staff(name, employee_no), staff_leave_types(name, is_paid)')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createStaffLeaveRequest(schoolId, { staffId, leaveTypeId, fromDate, toDate, reason }) {
  if (!staffId || !leaveTypeId || !fromDate || !toDate) throw new Error('Staff, leave type and dates are required');
  if (toDate < fromDate) throw new Error('To date is before from date');
  const days = Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1;
  const { data, error } = await supabase.from('staff_leave_requests').insert({
    school_id: schoolId, staff_id: staffId, leave_type_id: leaveTypeId,
    from_date: fromDate, to_date: toDate, days, reason: reason || null, status: 'pending',
  }).select().single();
  if (error) throw error;
  return data;
}

export async function reviewStaffLeaveRequest(id, status, userId, notes) {
  if (!['approved', 'rejected'].includes(status)) throw new Error('Invalid status');
  const { error } = await supabase.from('staff_leave_requests').update({
    status, approved_by: userId || null, approval_notes: notes || null,
    updated_at: new Date().toISOString(),
  }).eq('id', id);
  if (error) throw error;
}

export async function getStaffLeaveBalances(schoolId, year) {
  const { data: staffRows, error: sErr } = await supabase.from('staff').select('id').eq('school_id', schoolId);
  if (sErr) throw sErr;
  const ids = (staffRows || []).map(s => s.id);
  if (!ids.length) return [];
  let q = supabase.from('staff_leave_balance').select('*').in('staff_id', ids);
  if (year) q = q.eq('year', year);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// ─── DIARY ───────────────────────────────────────────────
export async function getRecentDiaryDatesForSections(schoolId, sectionIds) {
  if (!sectionIds.length) return [];
  const { data, error } = await supabase
    .from('diary_entries')
    .select('section_id, subject_id, date')
    .eq('school_id', schoolId)
    .in('section_id', sectionIds)
    .order('date', { ascending: false })
    .limit(2000);
  if (error) throw error;
  return data || [];
}

async function getAdminRecipients(schoolId) {
  const { data, error } = await supabase.from('users').select('id, roles(name)').eq('school_id', schoolId);
  if (error) throw error;
  return (data || []).filter(u => ['owner', 'admin'].includes(u.roles?.name));
}

export async function notifyDiaryChanged(schoolId, { sectionId, subjectId, actorUserId }) {
  const [{ data: sub }, { data: sec }, { data: actor }] = await Promise.all([
    supabase.from('subjects').select('name').eq('id', subjectId).maybeSingle(),
    supabase.from('sections').select('name, classes(name)').eq('id', sectionId).maybeSingle(),
    supabase.from('users').select('name').eq('id', actorUserId).maybeSingle(),
  ]);
  const subjectName = sub?.name || '—';
  const sectionLabel = sec?.classes?.name ? `${sec.classes.name} – ${sec.name}` : (sec?.name || '—');
  const actorName = actor?.name || 'A teacher';

  const recipients = await getAdminRecipients(schoolId);
  if (!recipients.length) return;

  const title = `Diary entry changed — ${subjectName}`;
  const message = `${actorName} changed the ${subjectName} diary entry for ${sectionLabel} today.`;
  const { error } = await supabase.from('notifications').insert(
    recipients.map(r => ({
      school_id: schoolId, user_id: r.id, title, message,
      type: 'diary_changed', reference_type: 'section_subject', reference_id: sectionId,
    }))
  );
  if (error) throw error;
}

export async function notifyMissedDiary(schoolId, { assignmentId, staffName, subjectName, sectionLabel, missedDays, lastEntryDate }) {
  const sinceIso = lastEntryDate || localISO(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const { data: already, error: checkErr } = await supabase
    .from('notifications')
    .select('id')
    .eq('school_id', schoolId)
    .eq('type', 'diary_gap')
    .eq('reference_id', assignmentId)
    .gte('created_at', sinceIso)
    .limit(1);
  if (checkErr) throw checkErr;
  if (already && already.length > 0) return;

  const recipients = await getAdminRecipients(schoolId);
  if (!recipients.length) return;

  const title = `Diary gap — ${subjectName}`;
  const message = `${staffName} hasn't written a diary entry for ${subjectName} (${sectionLabel}) in ${missedDays} day${missedDays === 1 ? '' : 's'}.`;
  const { error: insErr } = await supabase.from('notifications').insert(
    recipients.map(r => ({
      school_id: schoolId,
      user_id: r.id,
      title,
      message,
      type: 'diary_gap',
      reference_type: 'teacher_assignment',
      reference_id: assignmentId,
    }))
  );
  if (insErr) throw insErr;
}


// ─── ANNOUNCEMENTS ───────────────────────────────────────
export async function getAnnouncements(schoolId) {
  const { data, error } = await supabase
    .from('announcements')
    .select('*, users(name)')
    .eq('school_id', schoolId)
    .order('published_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function saveAnnouncement(announcement) {
  const { data, error } = await supabase.from('announcements').insert(announcement).select().single();
  if (error) throw error;
  return data;
}

// ─── NOTIFICATIONS ───────────────────────────────────────
export async function getNotifications(userId) {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return data;
}

export async function markNotificationRead(id) {
  const { error } = await supabase.from('notifications').update({ is_read: true, read_at: new Date() }).eq('id', id);
  if (error) throw error;
}

// ─── AUDIT LOG ───────────────────────────────────────────
export async function addAuditLog(schoolId, userId, action, objectType, objectId, oldData, newData) {
  const { error } = await supabase.from('audit_logs').insert({
    school_id: schoolId,
    user_id: userId,
    action,
    object_type: objectType,
    object_id: objectId,
    old_data: oldData,
    new_data: newData,
  });
  if (error) console.error('Audit log error:', error);
}

// ─── APPROVAL REQUESTS ───────────────────────────────────
export async function getApprovalRequests(schoolId, status = 'pending') {
  const { data, error } = await supabase
    .from('approval_requests')
    .select('*, approval_workflows(name,object_type), users!requested_by(name)')
    .eq('school_id', schoolId)
    .eq('status', status)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function approveRequest(id, userId, notes) {
  const { error } = await supabase.from('approval_requests').update({
    status: 'approved',
    approved_by: userId,
    approval_notes: notes,
    actioned_at: new Date(),
  }).eq('id', id);
  if (error) throw error;
}

export async function rejectRequest(id, userId, reason) {
  const { error } = await supabase.from('approval_requests').update({
    status: 'rejected',
    approved_by: userId,
    rejection_reason: reason,
    actioned_at: new Date(),
  }).eq('id', id);
  if (error) throw error;
}

// ─── USERS MANAGEMENT ────────────────────────────────────
export async function getUsers(schoolId) {
  const { data, error } = await supabase
    .from('users')
    .select('*, roles(name,display_name)')
    .eq('school_id', schoolId);
  if (error) throw error;
  return data;
}

export async function getRoles(schoolId) {
  const { data, error } = await supabase.from('roles').select('id, name').eq('school_id', schoolId);
  if (error) throw error;
  return data || [];
}

const SUPABASE_FUNCTIONS_URL = 'https://qnwokktdgpqilaooanfe.supabase.co/functions/v1';

async function edgeFunction(name, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not signed in');
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `${name} failed`);
  return data;
}

// roleName: 'owner' | 'admin' | 'accountant' | 'teacher' | 'parent' | 'student'
// linkStaffId / linkFamilyId / linkStudentId required for teacher / parent / student respectively
export async function createUser(payload) {
  return edgeFunction('create-user', payload);
}

// Platform admin only. { labels: string[], hours, redeemDays } -> { passes: [{ label, loginId, password, ... }], failed }
export async function createDemoPasses(payload) {
  return edgeFunction('create-demo-passes', payload);
}

// ─── WHATSAPP LOG ────────────────────────────────────────
export async function logWhatsApp(schoolId, toNumber, messageType, content, referenceId) {
  const { error } = await supabase.from('whatsapp_logs').insert({
    school_id: schoolId,
    to_number: toNumber,
    message_type: messageType,
    message_content: content,
    reference_id: referenceId,
    status: 'pending',
  });
  if (error) console.error('WhatsApp log error:', error);
}
// ════════════════════════════════════════════════════════
// ADD THESE TO src/db.js (append at end)
// ════════════════════════════════════════════════════════

// ─── ACCOUNTS ────────────────────────────────────────────
export async function getAccounts(schoolId) {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('code');
  if (error) throw error;
  return data;
}

// ─── JOURNAL ENTRY NUMBER ────────────────────────────────
// entry_no is assigned by the Postgres sequence journal_entry_seq via the
// column default — never computed client-side. The old count-then-insert
// version raced with the RPCs that also write journal entries; both counters
// reached 61 independently before this was caught.

// ─── POST JOURNAL ENTRY (double-entry, balance-enforced) ──
// entry: { school_id, date, reference_type, reference_id, narration, created_by }
// lines: [{ account_id, debit, credit, line_narration }]
// The DB trigger rejects unbalanced entries — this is the audit guarantee.
export async function postJournalEntry(entry, lines) {
  // Client-side pre-check (DB enforces too, but fail fast with clear msg)
  const totalDebit  = lines.reduce((s, l) => s + Number(l.debit  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  if (Math.round(totalDebit * 100) !== Math.round(totalCredit * 100)) {
    throw new Error(`Journal unbalanced: Dr ${totalDebit} ≠ Cr ${totalCredit}`);
  }
  if (lines.length < 2) throw new Error('Journal needs at least 2 lines');

  const { data: je, error: jeErr } = await supabase
    .from('journal_entries')
    .insert({ ...entry, is_posted: true })
    .select()
    .single();
  if (jeErr) throw jeErr;

  const { error: linesErr } = await supabase
    .from('journal_lines')
    .insert(lines.map(l => ({
      journal_entry_id: je.id,
      school_id: entry.school_id,
      account_id: l.account_id,
      debit: Number(l.debit || 0),
      credit: Number(l.credit || 0),
      line_narration: l.line_narration || null,
    })));
  if (linesErr) {
    // Roll back the header if lines fail (trigger may have rejected imbalance)
    await supabase.from('journal_entries').delete().eq('id', je.id);
    throw linesErr;
  }
  return je;
}
// ─── POST JOURNAL SAFE (wrapper — short call, never crashes UI) ──
export async function postJournalSafe({ schoolId, type, refId = null, narration = '', userId = null, lines = [], date = null }) {
  try {
    if (!schoolId || !lines.length) return null;
    const clean = lines.filter(l => l.account_id);
    if (clean.length < 2) { console.warn(`Journal (${type}) skipped: missing accounts`); return null; }
    return await postJournalEntry({
      school_id: schoolId,
      date: date || todayISO(),
      reference_type: type,
      reference_id: refId,
      narration,
      created_by: userId,
    }, clean);
  } catch (err) {
    console.error(`Journal (${type}) failed:`, err.message);
    return null;
  }
}

// ─── REVERSE JOURNAL ENTRY (for voids/corrections) ───────
// Creates a mirror entry (swap Dr<->Cr) referencing the original.
export async function reverseJournalEntry(originalEntryId, schoolId, createdBy, narration) {
  const { data: orig, error: oErr } = await supabase
    .from('journal_entries')
    .select('*, journal_lines(*)')
    .eq('id', originalEntryId)
    .single();
  if (oErr) throw oErr;
  if (orig.is_reversed) throw new Error('Entry already reversed');

  const reversedLines = (orig.journal_lines || []).map(l => ({
    account_id: l.account_id,
    debit: Number(l.credit || 0),   // swap
    credit: Number(l.debit || 0),
    line_narration: 'Reversal',
  }));

  const reversal = await postJournalEntry({
    school_id: schoolId,
    date: todayISO(),
    reference_type: 'reversal',
    reference_id: originalEntryId,
    narration: narration || `Reversal of ${orig.entry_no}`,
    created_by: createdBy,
  }, reversedLines);

  await supabase.from('journal_entries')
    .update({ is_reversed: true, reversed_by: reversal.id })
    .eq('id', originalEntryId);

  return reversal;
}

// ─── FEE ASSIGNMENTS ─────────────────────────────────────
export async function getFeeAssignments(schoolId, month, year) {
  const { data, error } = await supabase
    .from('fee_assignments')
    .select('*, students(name, admission_no), requested:requested_by(name), approved:approved_by(name)')
    .eq('school_id', schoolId)
    .eq('month', month)
    .eq('year', year)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createFeeAssignment(assignment) {
  const { data, error } = await supabase.from('fee_assignments').insert(assignment).select().single();
  if (error) throw error;
  return data;
}

export async function approveFeeAssignment(id, approvedBy) {
  const { error } = await supabase.from('fee_assignments')
    .update({ status: 'approved', approved_by: approvedBy, approved_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function approveFeeAssignmentsBatch(ids, approvedBy) {
  const { error } = await supabase.from('fee_assignments')
    .update({ status: 'approved', approved_by: approvedBy, approved_at: new Date().toISOString() })
    .in('id', ids);
  if (error) throw error;
}

export async function attachFeeAssignmentNow(assignment, familyId, month, year, userId) {
  // No challan yet for this family this period (e.g. student admitted mid-month,
  // after the bulk fee_generate run) — create one on demand instead of failing.
  const challan = await ensureChallanForFamily(assignment.school_id, familyId, month, year, userId);

  const { data: newItem, error: insErr } = await supabase
    .from('challan_items').insert({
      challan_id: challan.id, school_id: assignment.school_id,
      student_id: assignment.student_id, fee_head_id: assignment.fee_head_id || null,
      description: assignment.note ? `${assignment.label} (${assignment.note})` : assignment.label,
      amount: assignment.amount, net_amount: assignment.amount,
      discount_amount: 0, waiver_amount: 0,
      item_type: assignment.category, month, year, is_late_addition: true,
    }).select().single();
  if (insErr) throw insErr;

  const { data: c } = await supabase.from('challans')
    .select('net_payable, total_amount').eq('id', challan.id).single();
  await supabase.from('challans').update({
    net_payable: Number(c?.net_payable || 0) + Number(assignment.amount),
    total_amount: Number(c?.total_amount || 0) + Number(assignment.amount),
  }).eq('id', challan.id);

  await supabase.from('fee_assignments')
    .update({ status: 'attached', attached_challan_item_id: newItem.id })
    .eq('id', assignment.id);

  return newItem;
}

export async function attachFeeAssignmentsBatch(assignments, studentFamilyMap, month, year, userId) {
  const byFamily = {};
  const skipped = [];
  for (const a of assignments) {
    const fid = studentFamilyMap[a.student_id];
    if (!fid) { skipped.push(a); continue; }
    if (!byFamily[fid]) byFamily[fid] = [];
    byFamily[fid].push(a);
  }
  if (skipped.length) {
    console.error('attachFeeAssignmentsBatch: skipped rows with no resolvable family', skipped.map(a => a.id));
  }

  const results = await Promise.all(
    Object.entries(byFamily).map(async ([familyId, rows]) => {
      const out = [];
      for (const a of rows) {
        try {
          const item = await attachFeeAssignmentNow(
            { ...a, status: 'approved' }, familyId, month, year, userId
          );
          out.push(item);
        } catch (err) {
          if (err.message === 'NO_ACTIVE_CHALLAN') skipped.push({ ...a, reason: 'no_active_challan' });
          else throw err;
        }
      }
      return out;
    })
  );

  return { attached: results.flat(), skipped };
}

export async function deleteFeeAssignment(assignment) {
  // An attached assignment owns a live charge on the family ledger. Never hard-delete it:
  // that skips the journal and leaves the family totals stale. Void it through the same
  // RPC the ledger uses (unpaid charges only), then remove the assignment row.
  if (assignment.status === 'attached' && assignment.attached_challan_item_id) {
    const { data: item, error: itemErr } = await supabase
      .from('challan_items')
      .select('voided')
      .eq('id', assignment.attached_challan_item_id)
      .maybeSingle();
    if (itemErr) throw itemErr;
    if (item && !item.voided) {
      const { error: voidErr } = await supabase.rpc('fee_void_items', {
        p_item_ids: [assignment.attached_challan_item_id],
        p_reason: 'Fee assignment deleted',
      });
      if (voidErr) throw voidErr;
    }
  }
  const { error } = await supabase.from('fee_assignments').delete().eq('id', assignment.id);
  if (error) throw error;
}

export async function getApprovedFeeAssignments(schoolId, studentIds, month, year) {
  const { data, error } = await supabase
    .from('fee_assignments')
    .select('*')
    .eq('school_id', schoolId)
    .eq('status', 'approved')
    .eq('month', month)
    .eq('year', year)
    .in('student_id', studentIds);
  if (error) throw error;
  return data;
}

export async function markFeeAssignmentAttached(id, challanItemId) {
  const { error } = await supabase.from('fee_assignments')
    .update({ status: 'attached', attached_challan_item_id: challanItemId })
    .eq('id', id);
  if (error) throw error;
}

export async function markFeeAssignmentsAttachedBatch(rows) {
  // rows: [{ id, attached_challan_item_id }]
  if (!rows || !rows.length) return;
  const { error } = await supabase.from('fee_assignments')
    .upsert(rows.map(r => ({ id: r.id, status: 'attached', attached_challan_item_id: r.attached_challan_item_id })), { onConflict: 'id' });
  if (error) throw error;
}

// ─── FEES DATA (combined fetch for Collection tab) ───────
// Per-student aggregates for the Collection table — 242 rows instead of the
// 2400+ returned by getPeriodItems. The table only needs totals per student;
// the payment modal fetches real per-item rows on demand (see getFamilyItems).
// New O(families) query — reads pre-computed family_fee_accounts.
// Returns one row per family regardless of how many months or fee heads exist.
// Replaces get_period_summary which still computed from challan_items on every call.
export async function getFeeCollection(schoolId, month, year) {
  const { data, error } = await supabase
    .rpc('get_fee_collection', { p_school_id: schoolId, p_month: month, p_year: year });
  if (error) throw error;
  return data || [];
}

// Keep getPeriodSummary for backward compat during transition
export async function getPeriodSummary(schoolId, month, year) {
  return getFeeCollection(schoolId, month, year);
}

// Real challan_items for one family, fetched when the payment modal opens.
// These carry genuine challan_item_id values safe to pass to record_fee_payment.
export async function getFamilyItems(schoolId, month, year, studentIds) {
  if (!studentIds || !studentIds.length) return [];
  // Filter server-side via PostgREST — returns ~30 rows for a family instead of
  // fetching all 2400+ and filtering in the browser.
  const { data, error } = await supabase
    .rpc('get_period_items', { p_school_id: schoolId, p_month: month, p_year: year })
    .select('id,student_id,family_id,challan_id,description,item_type,month,year,net_amount,paid_amount,remaining_balance,challan_status,is_current_period,voided,waiver_amount,discount_amount')
    .in('student_id', studentIds);
  if (error) throw error;
  return data || [];
}

export async function getFeesData(schoolId, studentIds, month, year) {
  const [collection, advanceMap] = await Promise.all([
    getFeeCollection(schoolId, month + 1, year),
    getAdvanceBalances(schoolId),
  ]);
  return { challans: [], advanceMap, summary: collection, periodItems: [] };
}


export async function getChallanPeriods(schoolId) {
  const PAGE = 1000;
  let from = 0;
  let all = [];
  while (true) {
    const { data, error } = await supabase
      .from('challans')
      .select('month, year, status, months_count')
      .eq('school_id', schoolId)
      .order('year', { ascending: true })
      .order('month', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

export async function incrementChallanPaid(challanId, amount) {
  const { error } = await supabase.rpc('increment_challan_paid', { p_challan_id: challanId, p_amount: amount });
  if (error) throw error;
}

export async function closeChallans(schoolId, month, year) {
  const { error } = await supabase
    .from('challans')
    .update({ status: 'closed' })
    .eq('school_id', schoolId)
    .eq('month', month + 1)
    .eq('year', year);
  if (error) throw error;
}

export async function reopenChallans(schoolId, month, year) {
  const { error } = await supabase
    .from('challans')
    .update({ status: 'published' })
    .eq('school_id', schoolId)
    .eq('month', month + 1)
    .eq('year', year);
  if (error) throw error;
}

// ─── TRIAL BALANCE (audit report) ────────────────────────
export async function getTrialBalance(schoolId) {
  const { data, error } = await supabase
    .from('journal_lines')
    .select('debit, credit, accounts!inner(code, name, type, school_id)')
    .eq('accounts.school_id', schoolId);
  if (error) throw error;
  const map = {};
  (data || []).forEach(l => {
    const a = l.accounts;
    if (!map[a.code]) map[a.code] = { code: a.code, name: a.name, type: a.type, debit: 0, credit: 0 };
    map[a.code].debit  += Number(l.debit  || 0);
    map[a.code].credit += Number(l.credit || 0);
  });
  return Object.values(map).sort((x, y) => x.code.localeCompare(y.code));
}

// ─── LEDGER DATA ─────────────────────────────────────────
export async function getAllChallansForSchool(schoolId) {
  const PAGE = 1000;
  let from = 0;
  let all = [];
  while (true) {
    const { data, error } = await supabase
      .from('challans')
      .select('*, challan_items(*, students(name)), payments(*, payment_items(*)), users!challans_generated_by_fkey(name)')
      .eq('school_id', schoolId)
      .order('year', { ascending: true })
      .order('month', { ascending: true })
      .order('challan_no', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

export async function getLedgerData(schoolId) {
  const [challans, advanceRows, sales, assignments] = await Promise.all([
    getAllChallansForSchool(schoolId),
    supabase.from('advance_ledger').select('*, users:created_by(name)').eq('school_id', schoolId).then(r => { if (r.error) throw r.error; return r.data || []; }),
    supabase.from('sales').select('*, students(name, families(father_name)), users:sold_by(name)').eq('school_id', schoolId).then(r => { if (r.error) throw r.error; return r.data || []; }),
    supabase.from('fee_assignments').select('*, requested:requested_by(name)').eq('school_id', schoolId).not('attached_challan_item_id', 'is', null).then(r => { if (r.error) throw r.error; return r.data || []; }),
  ]);
  return { challans, advanceRows, sales, assignments };
}

// Scoped version of getLedgerData — fetches ledger rows for a specific set of
// students only, instead of the entire school. Returns the exact same shape as
// getLedgerData so the Ledger tab's existing rendering works unchanged.
export async function getLedgerDataForStudents(schoolId, studentIds) {
  if (!studentIds || !studentIds.length) {
    return { challans: [], advanceRows: [], sales: [], assignments: [] };
  }

  // Find which challans contain items for these students
  const { data: itemRows, error: itemErr } = await supabase
    .from('challan_items')
    .select('challan_id')
    .eq('school_id', schoolId)
    .in('student_id', studentIds);
  if (itemErr) throw itemErr;
  const challanIds = [...new Set((itemRows || []).map(r => r.challan_id).filter(Boolean))];

  const [challans, advanceRows, sales, assignments] = await Promise.all([
    challanIds.length
      ? supabase
          .from('challans')
          .select('*, challan_items(*, students(name)), payments(*, payment_items(*)), users!challans_generated_by_fkey(name)')
          .in('id', challanIds)
          .then(r => { if (r.error) throw r.error; return r.data || []; })
      : Promise.resolve([]),
    supabase.from('advance_ledger').select('*, users:created_by(name)').eq('school_id', schoolId).in('student_id', studentIds).then(r => { if (r.error) throw r.error; return r.data || []; }),
    supabase.from('sales').select('*, students(name, families(father_name)), users:sold_by(name)').eq('school_id', schoolId).in('student_id', studentIds).then(r => { if (r.error) throw r.error; return r.data || []; }),
    supabase.from('fee_assignments').select('*, requested:requested_by(name)').eq('school_id', schoolId).in('student_id', studentIds).not('attached_challan_item_id', 'is', null).then(r => { if (r.error) throw r.error; return r.data || []; }),
  ]);

  return { challans, advanceRows, sales, assignments };
}

export async function getSaleDetail(saleId) {
  const { data, error } = await supabase
    .from('sales')
    .select('*, sale_items(*), students(name, families(father_name))')
    .eq('id', saleId)
    .single();
  if (error) throw error;
  return {
    studentName: data.students?.name,
    fatherName: data.students?.families?.father_name || '',
    items: (data.sale_items || []).map(i => ({ name: i.product_name, qty: i.quantity, price: i.unit_price, total: i.total_price })),
    subtotal: Number(data.subtotal || 0),
    discountAmount: Number(data.discount_amount || 0),
    total: Number(data.total_amount || 0),
    amountPaid: Number(data.cash_received || 0),
    creditAmount: Number(data.credit_amount || 0),
    advanceApplied: 0,
  };
}

export async function recordPosSale(sale, items) {
  const { data, error } = await supabase.from('sales').insert(sale).select().single();
  if (error) throw error;
  if (items && items.length > 0) {
    const { error: itemErr } = await supabase.from('sale_items').insert(
      items.map(i => ({ ...i, sale_id: data.id }))
    );
    if (itemErr) {
      await supabase.from('sales').delete().eq('id', data.id);
      throw itemErr;
    }
  }
  return data;
}

// ─── VOID / WAIVER / ADVANCE ─────────────────────────────
export async function voidChallanItems(itemIds, reason, userId) {
  const { error } = await supabase.rpc('void_challan_items', {
    p_item_ids: itemIds, p_reason: reason, p_user_id: userId,
  });
  if (error) throw error;
}

export async function applyWaiver(schoolId, waivers, reason, userId) {
  const { error } = await supabase.rpc('apply_waiver', {
    p_school_id: schoolId, p_waivers: waivers, p_reason: reason, p_user_id: userId,
  });
  if (error) throw error;
}

export async function depositAdvance(studentId, amount, userId) {
  const { data, error } = await supabase.rpc('deposit_advance', {
    p_student_id: studentId, p_amount: amount, p_by: userId,
  });
  if (error) throw error;
  return data;
}

export async function drawAdvance(studentId, amount, by, refId, refType) {
  const { data, error } = await supabase.rpc('draw_advance', {
    p_student_id: studentId, p_amount: amount, p_by: by,
    p_reference_id: refId || null, p_reference_type: refType || null,
  });
  if (error) throw error;
  return data;
}

// ORPHANED as of the ledger-model migration — zero callers anywhere in src/.
// This wrote to challans.paid_amount via incrementChallanPaid(), a column the
// ledger rebuild retired as unreliable. LedgerTab.jsx (its last caller) now
// goes through useFeeStore.reversePayment -> fee_reverse_payment RPC instead.
// Left in place rather than deleted — flagging only, per instruction.
export async function reversePayment(paymentId, reason, userId, cashAccountId, recvAccountId) {
  const { data: payment, error: fetchErr } = await supabase
    .from('payments')
    .select('id, school_id, challan_id, amount_paid, payment_method, payment_date, is_reversed')
    .eq('id', paymentId)
    .single();
  if (fetchErr) throw fetchErr;
  if (payment.is_reversed) throw new Error('Payment already reversed');

  const { error: flagErr } = await supabase.from('payments').update({
    is_reversed: true, reversed_by: userId,
    reversed_at: new Date().toISOString(), reversal_reason: reason,
  }).eq('id', paymentId);
  if (flagErr) throw flagErr;

  if (payment.challan_id && Number(payment.amount_paid) > 0) {
    await incrementChallanPaid(payment.challan_id, -Number(payment.amount_paid));
  }

  await supabase.rpc('reverse_advance_for_payment', { p_payment_id: paymentId, p_by: userId });

  if (payment.school_id && cashAccountId && recvAccountId && Number(payment.amount_paid) > 0) {
    await postJournalEntry({
      school_id: payment.school_id, date: payment.payment_date,
      reference_type: 'payment_reversal', reference_id: paymentId,
      narration: `Payment reversal — ${reason || 'Reversed'}`,
      created_by: userId,
    }, [
      { account_id: recvAccountId, debit: Number(payment.amount_paid), credit: 0, line_narration: 'Receivable reinstated' },
      { account_id: cashAccountId, debit: 0, credit: Number(payment.amount_paid), line_narration: 'Cash/bank returned' },
    ]);
  }
}

// ─── APPROVAL SETTINGS ───────────────────────────────────
export async function getApprovalSettings(schoolId) {
  const { data, error } = await supabase
    .from('approval_settings').select('*').eq('school_id', schoolId);
  if (error) throw error;
  const out = {};
  for (const r of data || []) out[r.module] = r;
  return out;
}

export async function saveApprovalSetting(schoolId, module, patch) {
  const { error } = await supabase.from('approval_settings')
    .upsert({ school_id: schoolId, module, ...patch, updated_at: new Date().toISOString() },
             { onConflict: 'school_id,module' });
  if (error) throw error;
}

// ─── UNBILLED STUDENTS (admitted this period with no charges yet) ──
export async function getUnbilledStudents(schoolId, month, year) {
  const { data: billed, error: bErr } = await supabase
    .from('challan_items').select('student_id')
    .eq('school_id', schoolId).eq('month', month).eq('year', year);
  if (bErr) throw bErr;
  const billedIds = new Set((billed || []).map(r => r.student_id).filter(Boolean));

  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const endMonth = month === 12 ? 1 : month + 1;
  const endYear  = month === 12 ? year + 1 : year;
  const end = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;

  const { data: recent, error: sErr } = await supabase
    .from('students').select('id, name, admission_no, admission_date, grade_id, classes(name)')
    .eq('school_id', schoolId).eq('status', 'active')
    .gte('admission_date', start).lt('admission_date', end);
  if (sErr) throw sErr;
  return (recent || []).filter(s => !billedIds.has(s.id));
}

// ─── FEE ASSIGNMENT BATCH ────────────────────────────────
export async function createFeeAssignmentBatch(rows) {
  const { data, error } = await supabase.from('fee_assignments').insert(rows).select();
  if (error) throw error;
  return data || [];
}

// ─── BULK UPDATE ─────────────────────────────────────────
export async function bulkUpdateStudents(schoolId, studentIds, field, value) {
  const { error } = await supabase.rpc('bulk_update_students', {
    p_school_id: schoolId, p_student_ids: studentIds,
    p_field: field, p_value: value != null ? String(value) : null,
  });
  if (error) throw error;
}

export async function promoteStudents(schoolId, studentIds, toGradeId, toSectionId, feeAmounts, toAcademicYearId, graduate) {
  const { data, error } = await supabase.rpc('promote_students', {
    p_school_id: schoolId,
    p_student_ids: studentIds,
    p_to_grade_id: toGradeId,
    p_to_section_id: toSectionId,
    p_fee_amounts: feeAmounts,
    p_to_academic_year_id: toAcademicYearId,
    p_graduate: !!graduate,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Promotion failed');
  return data;
}

// ─── STUDENT DOCUMENTS ───────────────────────────────────
// Canonical required-doc list. AttachmentsTab and DocumentsTab both import this
// so the "missing docs" checker and the upload form always agree on types.
export const REQUIRED_STUDENT_DOCS = [
  'B-Form / Birth Certificate',
  'Transfer Certificate',
  'Guardian CNIC Copy',
  'Photograph',
];

export async function uploadStudentDocument(schoolId, studentId, file, documentType, userId) {
  const path = `${schoolId}/${studentId}/${Date.now()}_${file.name}`;
  const { error: upErr } = await supabase.storage
    .from('student-documents').upload(path, file);
  if (upErr) throw upErr;

  const { data, error } = await supabase.from('student_documents').insert({
    student_id: studentId, school_id: schoolId,
    document_type: documentType, file_name: file.name,
    file_url: path,
    file_size: file.size, mime_type: file.type, uploaded_by: userId,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function uploadStudentPhoto(schoolId, studentId, file) {
  const path = `${schoolId}/${studentId}.jpg`;
  const { error } = await supabase.storage.from('student-photos').upload(path, file, { upsert: true, contentType: 'image/jpeg' });
  if (error) throw error;
  const { data } = supabase.storage.from('student-photos').getPublicUrl(path);
  await supabase.from('students').update({ photo_url: data.publicUrl }).eq('id', studentId);
  return data.publicUrl;
}

export async function getStudentDocuments(studentId) {
  const { data, error } = await supabase.from('student_documents')
    .select('*').eq('student_id', studentId).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getSignedDocUrl(path) {
  const { data, error } = await supabase.storage
    .from('student-documents').createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}

export async function deleteStudentDocument(docId, filePath) {
  await supabase.storage.from('student-documents').remove([filePath]);
  const { error } = await supabase.from('student_documents').delete().eq('id', docId);
  if (error) throw error;
}

export async function getStudentHistory(studentId) {
  const { data, error } = await supabase
    .from('student_history')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getStudentChallans(studentId, schoolId) {
  const { data: items, error: e1 } = await supabase
    .from('challan_items')
    .select('challan_id, month, year, net_amount')
    .eq('student_id', studentId)
    .eq('school_id', schoolId)
    .not('voided', 'eq', true);
  if (e1) throw e1;
  if (!items?.length) return [];

  const challanIds = [...new Set(items.map(i => i.challan_id))];
  const amountMap = {};
  items.forEach(i => { amountMap[i.challan_id] = (amountMap[i.challan_id] || 0) + Number(i.net_amount || 0); });

  const { data: challans, error: e2 } = await supabase
    .from('challans')
    .select('id, challan_no, status, month, year, net_payable, paid_amount, generated_at, due_date')
    .in('id', challanIds)
    .order('year', { ascending: false })
    .order('month', { ascending: false });
  if (e2) throw e2;

  return (challans || []).map(c => ({ ...c, studentTotal: amountMap[c.id] || 0 }));
}

export async function getStudentExamResults(studentId, schoolId) {
  const { data, error } = await supabase
    .from('mark_entries')
    .select('exam_id, subject_id, marks, status, exams(name, start_date, total_marks), subjects(name)')
    .eq('student_id', studentId)
    .eq('school_id', schoolId)
    .eq('submitted', true);
  if (error) throw error;
  return data || [];
}

// Count of active students missing at least one required doc type.
// DB stores status as lowercase ('active').
export async function getStudentsMissingDocuments(schoolId, requiredCount) {
  const { data, error } = await supabase
    .from('students')
    .select('id, student_documents(document_type)')
    .eq('school_id', schoolId)
    .eq('status', 'active');
  if (error) throw error;
  return (data || []).filter(s => (s.student_documents || []).length < requiredCount).length;
}

// ─── EXAMS ───────────────────────────────────────────────
export async function getExams(schoolId) {
  const { data, error } = await supabase
    .from('exams')
    .select('id, name, title, start_date')
    .eq('school_id', schoolId)
    .order('start_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Paper Maker's saved templates — replaces the old localStorage-only store.
export async function getExamTemplates(schoolId) {
  const { data, error } = await supabase
    .from('exam_templates')
    .select('*')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveExamTemplate(schoolId, { id, name, data }) {
  const row = { school_id: schoolId, name, data, updated_at: new Date().toISOString() };
  if (id) {
    const { data: res, error } = await supabase
      .from('exam_templates').update(row).eq('id', id).select().single();
    if (error) throw error;
    return res;
  }
  const { data: res, error } = await supabase
    .from('exam_templates').insert(row).select().single();
  if (error) throw error;
  return res;
}

export async function deleteExamTemplate(id) {
  const { error } = await supabase.from('exam_templates').delete().eq('id', id);
  if (error) throw error;
}

// ─── REPORTS ─────────────────────────────────────────────
export async function getReportDefinitions(schoolId) {
  const { data, error } = await supabase
    .from('report_definitions')
    .select('*')
    .or(`school_id.eq.${schoolId},school_id.is.null`)
    .eq('is_active', true)
    .order('category')
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function runReport(dataSourceRef, params) {
  const { data, error } = await supabase.rpc(dataSourceRef, params);
  if (error) throw error;
  return data || [];
}

// ─── PRODUCTS (DB-backed) ────────────────────────────────
export async function getProductCategories(schoolId) {
  const { data, error } = await supabase
    .from('product_categories')
    .select('id, name')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function addProductCategory(schoolId, name) {
  const { data, error } = await supabase
    .from('product_categories')
    .insert({ school_id: schoolId, name: name.trim() })
    .select('id, name').single();
  if (error) throw error;
  return data;
}

export async function renameProductCategory(id, newName) {
  const { error } = await supabase
    .from('product_categories')
    .update({ name: newName.trim() })
    .eq('id', id);
  if (error) throw error;
}

export async function deactivateProductCategory(categoryId) {
  const { count } = await supabase
    .from('products')
    .select('id', { count: 'exact', head: true })
    .eq('category_id', categoryId);
  if (count > 0) {
    throw new Error(`Cannot deactivate — ${count} product(s) are in this category. Move them to a different category first.`);
  }
  const { error } = await supabase.from('product_categories').update({ is_active: false }).eq('id', categoryId);
  if (error) throw error;
}

// ─── ASSET CATEGORIES ────────────────────────────────────
export async function getAssetCategories(schoolId) {
  const { data, error } = await supabase
    .from('asset_categories')
    .select('id, name')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function addAssetCategory(schoolId, name) {
  const { data, error } = await supabase
    .from('asset_categories')
    .insert({ school_id: schoolId, name: name.trim() })
    .select('id, name').single();
  if (error) throw error;
  return data;
}

export async function renameAssetCategory(id, newName) {
  const { error } = await supabase
    .from('asset_categories')
    .update({ name: newName.trim() })
    .eq('id', id);
  if (error) throw error;
}

export async function deactivateAssetCategory(categoryId) {
  // assets.category is a text field (name), not FK — look up the name first
  const { data: cat } = await supabase.from('asset_categories').select('name').eq('id', categoryId).single();
  if (cat) {
    const { count } = await supabase
      .from('assets')
      .select('id', { count: 'exact', head: true })
      .eq('category', cat.name);
    if (count > 0) {
      throw new Error(`Cannot deactivate — ${count} asset(s) are in this category. Move them to a different category first.`);
    }
  }
  const { error } = await supabase.from('asset_categories').update({ is_active: false }).eq('id', categoryId);
  if (error) throw error;
}

// ─── LIST OPTIONS ─────────────────────────────────────────
export async function getListOptions(schoolId, listKey) {
  const { data, error } = await supabase
    .from('list_options')
    .select('id, value, sort_order')
    .eq('school_id', schoolId)
    .eq('list_key', listKey)
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function addListOption(schoolId, listKey, value) {
  const { data: existing } = await supabase
    .from('list_options')
    .select('sort_order')
    .eq('school_id', schoolId)
    .eq('list_key', listKey)
    .order('sort_order', { ascending: false })
    .limit(1);
  const nextOrder = (existing?.[0]?.sort_order || 0) + 1;
  const { data, error } = await supabase
    .from('list_options')
    .insert({ school_id: schoolId, list_key: listKey, value: value.trim(), sort_order: nextOrder })
    .select('id, value, sort_order').single();
  if (error) throw error;
  return data;
}

export async function previewRenameImpact(optionId) {
  const { data, error } = await supabase.rpc('count_list_option_usage', { p_option_id: optionId });
  if (error) throw error;
  return Number(data || 0);
}

export async function renameListOption(optionId, newValue) {
  const { data, error } = await supabase.rpc('rename_list_option', { p_option_id: optionId, p_new_value: newValue.trim() });
  if (error) throw error;
  return Number(data || 0);
}

export async function deactivateListOption(optionId) {
  const usageCount = await previewRenameImpact(optionId);
  if (usageCount > 0) {
    throw new Error(`Cannot deactivate — ${usageCount} record(s) still use this value. Reassign them first, or rename instead of deactivating.`);
  }
  const { error } = await supabase.from('list_options').update({ is_active: false }).eq('id', optionId);
  if (error) throw error;
}

export async function updateComplaintStatus(complaintId, newStatus, userId, note = null) {
  const { data: complaint, error: fetchError } = await supabase
    .from('complaints').select('school_id').eq('id', complaintId).single();
  if (fetchError) throw fetchError;

  const { error } = await supabase
    .from('complaints')
    .update({ status: newStatus })
    .eq('id', complaintId);
  if (error) throw error;

  const { error: logError } = await supabase
    .from('complaint_updates')
    .insert({ complaint_id: complaintId, school_id: complaint.school_id, status: newStatus, note, updated_by: userId });
  if (logError) throw logError;
}

export async function assignComplaint(complaintId, assignedToUserId) {
  const { error } = await supabase.from('complaints')
    .update({ assigned_to: assignedToUserId })
    .eq('id', complaintId);
  if (error) throw error;
}

export async function deleteComplaint(complaintId) {
  const { error } = await supabase.from('complaints').delete().eq('id', complaintId);
  if (error) throw error;
}

export async function getFeeCollectedInRange(schoolId, startDate, endDate) {
  const { data, error } = await supabase
    .from('payments')
    .select('amount_paid, is_reversed')
    .eq('school_id', schoolId)
    .gte('payment_date', startDate)
    .lte('payment_date', endDate);
  if (error) throw error;
  return (data || []).filter(p => !p.is_reversed).reduce((s, p) => s + Number(p.amount_paid || 0), 0);
}

export async function getHomeSummary(schoolId) {
  const now        = new Date();
  const today      = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
  const monthStart = today.slice(0, 7) + '-01';

  const [feeR, salesR, expR, attR, staffAttR, staffCntR, complaintsR, admR] = await Promise.allSettled([
    supabase.from('payments').select('amount_paid, is_reversed')
      .eq('school_id', schoolId).gte('payment_date', monthStart).lte('payment_date', today),
    supabase.from('sales').select('total_amount')
      .eq('school_id', schoolId).gte('created_at', monthStart + 'T00:00:00'),
    supabase.from('expense_entries').select('amount')
      .eq('school_id', schoolId).gte('expense_date', monthStart).lte('expense_date', today),
    supabase.from('attendance').select('status')
      .eq('school_id', schoolId).eq('date', today),
    supabase.from('staff_attendance').select('status')
      .eq('school_id', schoolId).eq('date', today),
    supabase.from('staff').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).eq('is_active', true),
    supabase.from('complaints').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).neq('status', 'resolved'),
    supabase.from('students').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).gte('admission_date', monthStart),
  ]);

  const feeData    = feeR.status      === 'fulfilled' ? (feeR.value.data      || []) : [];
  const salesData  = salesR.status    === 'fulfilled' ? (salesR.value.data    || []) : [];
  const expData    = expR.status      === 'fulfilled' ? (expR.value.data      || []) : [];
  const attData    = attR.status      === 'fulfilled' ? (attR.value.data      || []) : [];
  const staffAtt   = staffAttR.status === 'fulfilled' ? (staffAttR.value.data || []) : [];
  const staffCount = staffCntR.status === 'fulfilled' ? (staffCntR.value.count || 0) : 0;
  const openCount  = complaintsR.status === 'fulfilled' ? (complaintsR.value.count || 0) : 0;
  const newAdmissions = admR.status === 'fulfilled' ? (admR.value.count || 0) : 0;

  const feeCollected  = feeData.filter(p => !p.is_reversed).reduce((s, p) => s + Number(p.amount_paid || 0), 0);
  const posSales      = salesData.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const expensesTotal = expData.reduce((s, r) => s + Number(r.amount || 0), 0);

  const attPresent = attData.filter(r => r.status === 'present' || r.status === 'late').length;
  const attTotal   = attData.length;
  const attPct     = attTotal > 0 ? ((attPresent / attTotal) * 100).toFixed(1) + '%' : '—';

  const staffPresent = staffAtt.filter(r => r.status === 'present' || r.status === 'late').length;
  const denominator  = staffCount > 0 ? staffCount : staffAtt.length;
  const staffPct     = denominator > 0 ? Math.round((staffPresent / denominator) * 100) + '%' : '—';

  return { feeCollected, posSales, expensesTotal, attPct, staffPct, openComplaints: openCount, newAdmissions };
}

export async function getProducts(schoolId, includeInactive = false) {
  let q = supabase.from('products').select('*, product_categories(name)').eq('school_id', schoolId).eq('is_supply', false).order('name');
  if (!includeInactive) q = q.eq('is_active', true);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(p => ({
    id:         p.id,
    name:       p.name,
    barcode:    p.barcode || '',
    unit:       p.unit || 'piece',
    buyPrice:   Number(p.buy_price  || 0),
    sellPrice:  Number(p.sell_price || 0),
    saleable:   p.is_saleable ?? true,
    isActive:   p.is_active ?? true,
    minStock:   Number(p.min_stock  || 0),
    category:   p.product_categories?.name || '',
    categoryId: p.category_id || null,
    notes:      p.notes || '',
  }));
}

export async function getProductStock(schoolId) {
  const { data, error } = await supabase
    .from('product_stock_lots')
    .select('product_id, quantity_remaining')
    .eq('school_id', schoolId)
    .gt('quantity_remaining', 0);
  if (error) throw error;
  const stock = {};
  (data || []).forEach(r => {
    stock[r.product_id] = (stock[r.product_id] || 0) + Number(r.quantity_remaining);
  });
  return stock;
}

// ── Supplies: internal consumables bought on a GRN, kept as hidden products ──
export async function getSupplies(schoolId) {
  const [{ data: items, error }, { data: lots, error: e2 }] = await Promise.all([
    supabase.from('products').select('id, name, unit, min_stock, is_active').eq('school_id', schoolId).eq('is_supply', true).eq('is_active', true).order('name'),
    supabase.from('product_stock_lots').select('id, product_id, quantity_remaining, unit_cost, note, received_at').eq('school_id', schoolId).gt('quantity_remaining', 0).order('received_at'),
  ]);
  if (error) throw error;
  if (e2) throw e2;
  return (items || []).map(p => {
    const l = (lots || []).filter(x => x.product_id === p.id).map(x => ({ id: x.id, qty: Number(x.quantity_remaining), cost: Number(x.unit_cost), note: x.note || '' }));
    return {
      id: p.id, name: p.name, unit: p.unit || 'piece', minStock: Number(p.min_stock || 0), lots: l,
      qty: l.reduce((a, x) => a + x.qty, 0), value: l.reduce((a, x) => a + x.qty * x.cost, 0),
    };
  });
}

// Returns the id of the supply with this name (any case), creating it when new.
export async function findOrCreateSupply(schoolId, name, unit) {
  const clean = String(name || '').trim();
  if (!clean) throw new Error('Enter a name for the supply');
  const { data: found, error } = await supabase.from('products').select('id, is_supply, is_active')
    .eq('school_id', schoolId).ilike('name', clean.replace(/[%_]/g, m => '\\' + m)).limit(5);
  if (error) throw error;
  const hit = (found || []).find(p => p.is_supply);
  if (hit) {
    if (!hit.is_active) await supabase.from('products').update({ is_active: true }).eq('id', hit.id);
    return hit.id;
  }
  if ((found || []).length) throw new Error(`"${clean}" already exists as a product. Pick a different supply name.`);
  const { data, error: e2 } = await supabase.from('products').insert({
    school_id: schoolId, name: clean, unit: String(unit || '').trim() || 'piece', buy_price: 0, sell_price: 0,
    is_saleable: false, is_supply: true, min_stock: 0, is_active: true,
  }).select('id').single();
  if (e2) throw e2;
  return data.id;
}

export async function setSupplyMinStock(productId, minStock) {
  const { error } = await supabase.from('products').update({ min_stock: Number(minStock) || 0 }).eq('id', productId).eq('is_supply', true);
  if (error) throw error;
}

export async function setGrnLotNotes(purchaseId, notes) {
  if (!notes.length) return;
  const { error } = await supabase.rpc('set_grn_lot_notes', { p_purchase_id: purchaseId, p_notes: notes });
  if (error) throw error;
}

export async function useSupplyStock(productId, quantity, date, note) {
  const { data, error } = await supabase.rpc('use_supply_stock', { p_product_id: productId, p_quantity: quantity, p_date: date, p_note: note || null });
  if (error) throw error;
  return Number(data || 0);
}

export async function postSupplyCount(counts, date) {
  const { data, error } = await supabase.rpc('post_supply_count', { p_counts: counts, p_date: date });
  if (error) throw error;
  return data;
}

export async function getSupplyUsage(schoolId, limit = 200) {
  const { data, error } = await supabase.from('supply_usage').select('id, quantity, cost, used_on, source, note, created_at, products(name, unit)')
    .eq('school_id', schoolId).order('used_on', { ascending: false }).order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []).map(r => ({ id: r.id, name: r.products?.name || '?', unit: r.products?.unit || '', qty: Number(r.quantity), cost: Number(r.cost), date: r.used_on, source: r.source, note: r.note || '' }));
}

export async function saveProduct(schoolId, product) {
  const row = {
    school_id:   schoolId,
    name:        product.name,
    barcode:     product.barcode  || null,
    unit:        product.unit     || 'piece',
    buy_price:   product.buyPrice,
    sell_price:  product.sellPrice,
    is_saleable: product.saleable ?? true,
    min_stock:   product.minStock,
    category_id: product.categoryId || null,
    notes:       product.notes || null,
    is_active:   true,
    updated_at:  new Date().toISOString(),
  };
  if (product.id) {
    const { error } = await supabase.from('products').update(row).eq('id', product.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('products').insert(row);
    if (error) throw error;
  }
}

const PRODUCT_FK_TABLES = ['purchase_items', 'sale_items', 'product_stock_lots', 'purchase_adjustments', 'purchase_order_items'];

export async function canDeleteProduct(productId) {
  const results = await Promise.all(
    PRODUCT_FK_TABLES.map(table =>
      supabase.from(table).select('id', { count: 'exact', head: true }).eq('product_id', productId)
        .then(r => r.count || 0).catch(() => 0)
    )
  );
  const blockingTables = PRODUCT_FK_TABLES.filter((_, i) => results[i] > 0);
  return { canDelete: blockingTables.length === 0, blockingTables };
}

export async function deleteProduct(productId) {
  const { error } = await supabase.from('products').delete().eq('id', productId);
  if (error) throw error;
}

export async function discontinueProduct(productId) {
  const { error } = await supabase.from('products')
    .update({ is_active: false, is_saleable: false, updated_at: new Date().toISOString() })
    .eq('id', productId);
  if (error) throw error;
}

export async function reactivateProduct(productId) {
  const { error } = await supabase.from('products')
    .update({ is_active: true, updated_at: new Date().toISOString() })
    .eq('id', productId);
  if (error) throw error;
}

// ─── DOCUMENTS ───────────────────────────────────────────
export async function getDocumentTemplates(schoolId) {
  const { data, error } = await supabase
    .from('document_templates')
    .select('*')
    .or(`school_id.eq.${schoolId},school_id.is.null`)
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function getNextDocumentNo(schoolId, docType, prefix) {
  const { data, error } = await supabase.rpc('get_next_document_no', {
    p_school_id: schoolId,
    p_doc_type:  docType,
    p_prefix:    prefix,
  });
  if (error) throw error;
  return data;
}

export async function issueDocument(schoolId, studentId, docType, documentNo, snapshot, issuedBy) {
  const { data, error } = await supabase
    .from('document_issues')
    .insert({
      school_id:   schoolId,
      student_id:  studentId,
      doc_type:    docType,
      document_no: documentNo,
      snapshot,
      issued_by:   issuedBy,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getFeeDefaulterCount(schoolId) {
  const [unpaidItems, students] = await Promise.all([
    getUnpaidItems(schoolId),
    supabase.from('students').select('id').eq('school_id', schoolId).eq('status', 'Active'),
  ]);
  const activeIds = new Set((students.data || []).map(s => s.id));
  const defaulterIds = new Set(
    unpaidItems.filter(i => activeIds.has(i.student_id) && Number(i.remaining_balance) > 0)
      .map(i => i.student_id)
  );
  return defaulterIds.size;
}

// ─── TIMETABLE ────────────────────────────────────────────

export async function getAcademicYears(schoolId) {
  const { data, error } = await supabase.from('academic_years')
    .select('id, name, is_current, start_date, end_date').eq('school_id', schoolId).order('start_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createAcademicSession(schoolId, name, startDate, endDate, opts = {}) {
  const { data, error } = await supabase.rpc('create_academic_session', {
    p_school_id: schoolId,
    p_name: name,
    p_start: startDate,
    p_end: endDate,
    p_copy_teacher_assignments: opts.teacherAssignments !== false,
    p_copy_subject_links: opts.subjectLinks !== false,
    p_copy_fee_structures: opts.feeStructures !== false,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Could not create the session');
  return data;
}

export async function activateAcademicSession(schoolId, yearId) {
  const { data, error } = await supabase.rpc('activate_academic_session', {
    p_school_id: schoolId, p_year_id: yearId,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Could not activate the session');
  return data;
}

// Row counts for the carry-forward checkboxes.
export async function getSessionCarryCounts(schoolId, yearId) {
  if (!yearId) return { teacherAssignments: 0, subjectLinks: 0, feeStructures: 0 };
  const [ta, sl, fs] = await Promise.all([
    supabase.from('teacher_assignments').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).eq('academic_year_id', yearId),
    supabase.from('subject_links').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).eq('academic_year_id', yearId),
    supabase.from('fee_structures').select('id', { count: 'exact', head: true })
      .eq('school_id', schoolId).eq('academic_year_id', yearId),
  ]);
  return {
    teacherAssignments: ta.count || 0,
    subjectLinks: sl.count || 0,
    feeStructures: fs.count || 0,
  };
}

export async function getTimetables(schoolId, academicYearId) {
  const { data, error } = await supabase.from('timetables')
    .select('*').eq('school_id', schoolId).eq('academic_year_id', academicYearId)
    .eq('is_active', true).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getExistingDraftTimetable(schoolId, academicYearId) {
  const { data } = await supabase.from('timetables')
    .select('*')
    .eq('school_id', schoolId)
    .eq('academic_year_id', academicYearId)
    .eq('status', 'draft')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data || null;
}

export async function createTimetable(schoolId, academicYearId, name, mode, dayMode, periodConfig, sectionIds, effectiveFrom, userId) {
  const { data, error } = await supabase.from('timetables').insert({
    school_id: schoolId, academic_year_id: academicYearId, name, mode, day_mode: dayMode,
    period_config: periodConfig, section_ids: sectionIds,
    effective_from: effectiveFrom, created_by: userId,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function getTeacherTree(schoolId, sectionIds, academicYearId) {
  let q = supabase.from('teacher_assignments')
    .select('teacher_id, subject_id, section_id, staff(name), subjects(name, code)')
    .eq('school_id', schoolId);
  if (academicYearId) q = q.eq('academic_year_id', academicYearId);
  if (sectionIds && sectionIds.length > 0) q = q.in('section_id', sectionIds);
  const { data, error } = await q;
  if (error) throw error;
  const tree = {};
  (data || []).forEach(r => {
    if (!tree[r.teacher_id]) tree[r.teacher_id] = { name: r.staff?.name || '—', subjects: [] };
    let sub = tree[r.teacher_id].subjects.find(s => s.id === r.subject_id);
    if (!sub) {
      sub = { id: r.subject_id, name: r.subjects?.name || '—', code: r.subjects?.code || '', sectionIds: [] };
      tree[r.teacher_id].subjects.push(sub);
    }
    sub.sectionIds.push(r.section_id);
  });
  return tree;
}

export async function getTimetableEntries(timetableId) {
  const { data, error } = await supabase.from('timetable_entries')
    .select('*, staff(name), subjects(name, code), sections(name, classes(name))')
    .eq('timetable_id', timetableId).eq('is_active', true);
  if (error) throw error;
  return data || [];
}

export async function assignSlot(schoolId, timetableId, sectionId, day, period, teacherId, subjectId) {
  const { data: existing } = await supabase.from('timetable_entries')
    .select('id').eq('timetable_id', timetableId).eq('section_id', sectionId)
    .eq('day_of_week', day).eq('period_index', period)
    .order('group_index').limit(1).maybeSingle();

  const { data: conflicts } = await supabase.rpc('check_teacher_conflict', {
    p_timetable_id: timetableId, p_teacher_id: teacherId, p_day: day, p_period: period,
    p_exclude_entry_id: existing?.id || null,
  });
  if (conflicts && conflicts.length > 0) {
    return { success: false, conflict: conflicts[0] };
  }
  const { data: canCombine } = await supabase.rpc('check_combined_lecture_cap', {
    p_timetable_id: timetableId, p_section_id: sectionId, p_teacher_id: teacherId,
    p_subject_id: subjectId, p_day: day, p_period: period,
  });
  if (canCombine === false) {
    return { success: false, error: 'Cannot combine more than 2 consecutive periods for the same teacher and subject' };
  }
  const { error } = await supabase.from('timetable_entries').upsert({
    school_id: schoolId, timetable_id: timetableId, section_id: sectionId,
    day_of_week: day, period_index: period, teacher_id: teacherId, subject_id: subjectId,
    is_active: true, updated_at: new Date().toISOString(),
  }, { onConflict: 'timetable_id,section_id,day_of_week,period_index,group_index' });
  if (error) throw error;
  return { success: true };
}

// Drag from the teacher list: put one teacher+subject into a cell, replacing whatever was there.
// No clash check; the grid marks teacher clashes in red.
export async function placeSubject(schoolId, timetableId, sectionId, day, period, teacherId, subjectId) {
  const now = new Date().toISOString();
  const { error: e1 } = await supabase.from('timetable_entries')
    .update({ is_active: false, updated_at: now })
    .eq('timetable_id', timetableId).eq('section_id', sectionId)
    .eq('day_of_week', day).eq('period_index', period).gt('group_index', 0);
  if (e1) throw e1;
  const { error: e2 } = await supabase.from('timetable_entries').upsert({
    school_id: schoolId, timetable_id: timetableId, section_id: sectionId,
    day_of_week: day, period_index: period, group_index: 0,
    teacher_id: teacherId, subject_id: subjectId, block_id: null, is_active: true, updated_at: now,
  }, { onConflict: 'timetable_id,section_id,day_of_week,period_index,group_index' });
  if (e2) throw e2;
}

export async function clearSlot(entryId) {
  const { error } = await supabase.from('timetable_entries')
    .update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', entryId);
  if (error) throw error;
}

export async function swapTeacher(entryId, schoolId, newTeacherId, newSubjectId, reason, userId) {
  const { data: entry, error: fetchErr } = await supabase.from('timetable_entries')
    .select('*').eq('id', entryId).single();
  if (fetchErr) throw fetchErr;
  await supabase.from('timetable_entry_changes').insert({
    entry_id: entryId, school_id: schoolId,
    old_teacher_id: entry.teacher_id, new_teacher_id: newTeacherId,
    old_subject_id: entry.subject_id, new_subject_id: newSubjectId,
    reason: reason || null, changed_by: userId,
  });
  const { error } = await supabase.from('timetable_entries')
    .update({ teacher_id: newTeacherId, subject_id: newSubjectId, updated_at: new Date().toISOString() })
    .eq('id', entryId);
  if (error) throw error;
}

export async function publishTimetable(timetableId) {
  const { error } = await supabase.from('timetables').update({ status: 'published' }).eq('id', timetableId);
  if (error) throw error;
}

export async function deleteTimetable(timetableId) {
  const { error: e1 } = await supabase.from('timetable_linked_blocks')
    .delete().eq('timetable_id', timetableId);
  if (e1) throw e1;
  const { data: entries } = await supabase.from('timetable_entries')
    .select('id').eq('timetable_id', timetableId);
  if (entries?.length) {
    const { error: e2 } = await supabase.from('timetable_entry_changes')
      .delete().in('entry_id', entries.map(e => e.id));
    if (e2) throw e2;
  }
  const { error: e3 } = await supabase.from('timetables').delete().eq('id', timetableId);
  if (e3) throw e3;
}

export async function updateTeacherAssignmentPeriods(id, periodsPerWeek) {
  const { error } = await supabase.from('teacher_assignments')
    .update({ periods_per_week: periodsPerWeek }).eq('id', id);
  if (error) throw error;
}

export async function autoCreateTimetable(timetableId, schoolId) {
  const { data, error } = await supabase.rpc('auto_create_timetable', {
    p_timetable_id: timetableId, p_school_id: schoolId, p_preview: false,
  });
  if (error) throw error;
  return data;
}

export async function previewAutoFill(timetableId, schoolId) {
  const { data, error } = await supabase.rpc('auto_create_timetable', {
    p_timetable_id: timetableId, p_school_id: schoolId, p_preview: true,
  });
  if (error) throw error;
  return data;
}

export async function addLinkedBlock(schoolId, timetableId, blockName, members) {
  const { error } = await supabase.from('timetable_linked_blocks')
    .insert({ school_id: schoolId, timetable_id: timetableId, block_name: blockName, members });
  if (error) throw error;
}

export async function getLinkedBlocks(timetableId) {
  const { data, error } = await supabase.from('timetable_linked_blocks')
    .select('*').eq('timetable_id', timetableId).order('created_at');
  if (error) throw error;
  return data || [];
}

export async function getSectionSubjects(sectionId, academicYearId) {
  const { data, error } = await supabase.from('teacher_assignments')
    .select('subject_id, teacher_id, subjects(name, code), staff(name)')
    .eq('section_id', sectionId).eq('academic_year_id', academicYearId);
  if (error) throw error;
  const mapped = (data || []).map(r => ({
    subjectId: r.subject_id,
    subjectName: r.subjects?.name || '—',
    subjectCode: r.subjects?.code || '',
    teacherId: r.teacher_id,
    teacherName: r.staff?.name || '—',
  }));
  const seen = new Map();
  mapped.forEach(r => { if (!seen.has(r.subjectId)) seen.set(r.subjectId, r); });
  return [...seen.values()].sort((a, b) => a.subjectName.localeCompare(b.subjectName));
}

export async function getSubjectLinks(sectionIds, academicYearId) {
  const { data, error } = await supabase
    .from('subject_links')
    .select('*')
    .in('section_id', sectionIds)
    .eq('academic_year_id', academicYearId);
  if (error) throw error;
  return data || [];
}

export async function addSubjectLink(schoolId, sectionId, academicYearId, subjectIds) {
  const { data, error } = await supabase
    .from('subject_links')
    .insert({ school_id: schoolId, section_id: sectionId, academic_year_id: academicYearId, subject_ids: subjectIds })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSubjectLink(id) {
  const { error } = await supabase.from('subject_links').delete().eq('id', id);
  if (error) throw error;
}

export async function assignMergedSlot(schoolId, timetableId, sectionId, day, period, subjectIds, academicYearId, partnerSectionId) {
  const { data, error } = await supabase.rpc('assign_merged_slot', {
    p_school_id: schoolId, p_timetable_id: timetableId, p_section_id: sectionId,
    p_day: day, p_period: period, p_subject_ids: subjectIds,
    p_academic_year_id: academicYearId,
    p_partner_section_id: partnerSectionId || null,
  });
  if (error) throw error;
  return data;
}

export async function swapSlots(timetableId, sectionId, day, periodA, periodB) {
  const { data, error } = await supabase.rpc('swap_slots', {
    p_timetable_id: timetableId, p_section_id: sectionId,
    p_day: day, p_a: periodA, p_b: periodB,
  });
  if (error) throw error;
  return data;
}

// Drag and drop: swaps two periods with no clash check; the grid marks teacher clashes in red
export async function swapSlotsForce(timetableId, sectionId, day, periodA, periodB) {
  const { data, error } = await supabase.rpc('swap_slots_force', {
    p_timetable_id: timetableId, p_section_id: sectionId,
    p_day: day, p_a: periodA, p_b: periodB,
  });
  if (error) throw error;
  return data;
}

export async function clearAllEntries(timetableId) {
  const { data, error } = await supabase.from('timetable_entries')
    .delete().eq('timetable_id', timetableId).select();
  if (error) throw error;
  return data || [];
}

export async function restoreEntries(rows) {
  if (!rows || rows.length === 0) return;
  const clean = rows.map(r => ({
    id: r.id, school_id: r.school_id, timetable_id: r.timetable_id,
    section_id: r.section_id, day_of_week: r.day_of_week,
    period_index: r.period_index, group_index: r.group_index,
    teacher_id: r.teacher_id, subject_id: r.subject_id, is_active: r.is_active,
  }));
  const { error } = await supabase.from('timetable_entries').insert(clean);
  if (error) throw error;
}

// ─── DELETE GUARDS ────────────────────────────────────────
async function _count(table, col, id) {
  try {
    const r = await supabase.from(table).select('id', { count: 'exact', head: true }).eq(col, id);
    return r.count || 0;
  } catch { return 0; }
}

function _makeGuard(fkTables, fkColumn, extras = []) {
  return async (id) => {
    const counts   = await Promise.all(fkTables.map(t => _count(t, fkColumn, id)));
    const exCounts = await Promise.all(extras.map(({ check }) => check(id).catch(() => 0)));
    const blocking = [
      ...fkTables.filter((_, i) => counts[i] > 0),
      ...extras.map(({ table }, i) => exCounts[i] > 0 ? table : null).filter(Boolean),
    ];
    return { canDelete: blocking.length === 0, blockingTables: blocking };
  };
}

const _CLASS_FK = ['date_sheet_entries','exam_subjects','fee_structures','mark_entries','sections'];
export const canDeleteClass = _makeGuard(_CLASS_FK, 'class_id', [
  { table: 'students', check: async (id) => (await supabase.from('students').select('id', { count: 'exact', head: true }).eq('grade_id', id)).count || 0 },
]);
export async function deactivateClass(id) {
  const { error } = await supabase.from('classes').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

const _FEE_HEAD_FK = ['challan_items','fee_assignments','fee_structure_items','fee_waivers','student_fee_assignments'];
export const canDeleteFeeHead = _makeGuard(_FEE_HEAD_FK, 'fee_head_id');
export async function deactivateFeeHead(id) {
  const { error } = await supabase.from('fee_heads').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

const _SECTION_FK = ['attendance','diary_entries','exam_papers','homework','teacher_assignments','timetable_entries'];
export const canDeleteSection = _makeGuard(_SECTION_FK, 'section_id', [
  { table: 'students', check: async (id) => (await supabase.from('students').select('id', { count: 'exact', head: true }).eq('section_id', id)).count || 0 },
]);
export async function deactivateSection(id) {
  const { error } = await supabase.from('sections').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

export async function canDeleteStaff(id) {
  const checks = [
    ['payroll_items','staff_id'],['salary_structures','staff_id'],
    ['staff_advances','staff_id'],['staff_attendance','staff_id'],
    ['staff_leave_requests','staff_id'],['staff_loans','staff_id'],
    ['academy_payroll_items','staff_id'],['academy_attendance','staff_id'],
    ['teacher_assignments','teacher_id'],['timetable_entries','teacher_id'],
  ];
  const counts = await Promise.all(checks.map(([t, col]) => _count(t, col, id)));
  const [cOld, cNew] = await Promise.all([
    _count('timetable_entry_changes','old_teacher_id', id),
    _count('timetable_entry_changes','new_teacher_id', id),
  ]);
  const blocking = checks.filter((_, i) => counts[i] > 0).map(([t]) => t);
  if (cOld + cNew > 0) blocking.push('timetable_entry_changes');
  return { canDelete: blocking.length === 0, blockingTables: blocking };
}
export async function deactivateStaff(id) {
  const { error } = await supabase.from('staff').update({ status: 'Deactivated' }).eq('id', id);
  if (error) throw error;
}

export async function createStaffLogin(schoolId, staffId, { email, name, password, phone }, accessToken) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/create-user`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ email, name, password, phone, roleName: 'teacher', schoolId, linkStaffId: staffId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Login creation failed');
  return data;
}

export async function canDeleteSubject(id) {
  const tables = ['class_subjects','date_sheet_entries','diary_entries','exam_papers','exam_subjects',
    'homework','mark_entries','question_bank','teacher_assignments','timetable_entries'];
  const counts = await Promise.all(tables.map(t => _count(t, 'subject_id', id)));
  const [cOld, cNew] = await Promise.all([
    _count('timetable_entry_changes','old_subject_id', id),
    _count('timetable_entry_changes','new_subject_id', id),
  ]);
  const blocking = tables.filter((_, i) => counts[i] > 0);
  if (cOld + cNew > 0) blocking.push('timetable_entry_changes');
  return { canDelete: blocking.length === 0, blockingTables: blocking };
}
export async function deactivateSubject(id) {
  const { error } = await supabase.from('subjects').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

const _VENDOR_FK = ['assets','purchase_adjustments','purchase_orders','purchases','vendor_payments'];
export const canDeleteVendor = _makeGuard(_VENDOR_FK, 'vendor_id');
export async function deleteVendor(id) {
  const { error } = await supabase.from('vendors').delete().eq('id', id);
  if (error) throw error;
}
export async function deactivateVendor(id) {
  const { error } = await supabase.from('vendors').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

// ─── DIARY ────────────────────────────────────────────────

export async function getTeacherSections(schoolId, teacherId) {
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) return [];
  const { data, error } = await supabase
    .from('teacher_assignments')
    .select('id, section_id, subject_id, sections(id, name, class_id, classes(name)), subjects(id, name)')
    .eq('school_id', schoolId)
    .eq('teacher_id', teacherId)
    .eq('academic_year_id', yearId);
  if (error) throw error;
  return data || [];
}

export async function getClassTeacherSections(schoolId, staffId) {
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) return [];
  const { data, error } = await supabase
    .from('teacher_assignments')
    .select('id, section_id, sections(id, name, classes(id, name))')
    .eq('school_id', schoolId)
    .eq('teacher_id', staffId)
    .eq('is_class_teacher', true)
    .eq('academic_year_id', yearId);
  if (error) throw error;
  // One assignment row per subject means the same section repeats (up to 9 times).
  // Callers loop over this list to fetch and subscribe, so return each section once.
  return [...new Map((data || []).map(r => [r.section_id, r])).values()];
}

export async function getClassSubjects(schoolId, classId) {
  if (!classId) return [];
  const { data, error } = await supabase
    .from('class_subjects')
    .select('subject_id, subjects(id, name)')
    .eq('school_id', schoolId)
    .eq('class_id', String(classId))
    .eq('is_active', true);
  if (error) throw error;
  return (data || [])
    .map(r => ({ id: r.subject_id, name: r.subjects?.name || '—' }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function getSectionDiaryEntries(schoolId, sectionId, date) {
  const { data, error } = await supabase
    .from('diary_entries')
    .select('id, subject_id, content, attachment_url, is_visible_to_parents, written_by, updated_at, created_at')
    .eq('school_id', schoolId)
    .eq('section_id', sectionId)
    .eq('date', date);
  if (error) throw error;
  return data || [];
}

export async function getPendingHomeworkForSection(schoolId, sectionId) {
  const today = todayISO();
  const { data, error } = await supabase
    .from('homework')
    .select('id, title, description, subject_id, attachment_url, due_date, subjects!fk_homework_subject(name)')
    .eq('school_id', schoolId)
    .eq('section_id', sectionId)
    .or(`due_date.gte.${today},due_date.is.null`)
    .order('due_date', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return data || [];
}

export async function getDiaryEntry(sectionId, subjectId, date) {
  const { data, error } = await supabase
    .from('diary_entries')
    .select('*')
    .eq('section_id', sectionId)
    .eq('subject_id', subjectId)
    .eq('date', date)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function saveDiaryEntry(schoolId, sectionId, subjectId, date, content, isVisibleToParents, userId, existingId, attachmentUrl) {
  const row = {
    school_id: schoolId,
    section_id: sectionId,
    subject_id: subjectId,
    date,
    content,
    is_visible_to_parents: isVisibleToParents,
    written_by: userId,
    updated_at: new Date().toISOString(),
  };
  if (attachmentUrl !== undefined) row.attachment_url = attachmentUrl;
  if (existingId) {
    const { error } = await supabase.from('diary_entries').update(row).eq('id', existingId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('diary_entries').insert(row);
    if (error) throw error;
  }
}

export async function uploadDiaryAttachment(schoolId, file) {
  let compressed = file;
  if (file.type.startsWith('image/')) {
    const { compressImage } = await import('./helpers/compressImage');
    compressed = await compressImage(file);
  }
  const ext = compressed.name.split('.').pop();
  const path = `${schoolId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('diary-attachments').upload(path, compressed);
  if (error) throw error;
  const { data } = supabase.storage.from('diary-attachments').getPublicUrl(path);
  return data.publicUrl;
}

export async function saveHomework(schoolId, sectionId, subjectId, title, description, attachmentUrl, assignedDate, dueDate, maxMarks, userId) {
  const { error } = await supabase.from('homework').insert({
    school_id: schoolId,
    section_id: sectionId,
    subject_id: subjectId,
    title,
    description: description || null,
    attachment_url: attachmentUrl || null,
    assigned_date: assignedDate,
    due_date: dueDate || null,
    max_marks: maxMarks || null,
    assigned_by: userId,
  });
  if (error) throw error;
}

export async function getHomeworkForSection(sectionId) {
  const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('homework')
    .select('*')
    .eq('section_id', sectionId)
    .order('assigned_date', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    attachment_url: row.created_at && row.created_at < tenDaysAgo ? null : row.attachment_url,
  }));
}

export async function getRecentDiaryEntries(schoolId, teacherId, days = 14) {
  const since = localISO(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
  const { data, error } = await supabase
    .from('diary_entries')
    .select('*, sections!fk_diary_entries_section(id, name, classes(name)), subjects!fk_diary_entries_subject(id, name)')
    .eq('school_id', schoolId)
    .eq('written_by', teacherId)
    .gte('date', since)
    .order('date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export function runOpportunisticCleanup() {
  supabase.rpc('cleanup_expired_diary_attachments').then(() => {}).catch(() => {});
}

// ─── FEE PAYMENT (checkbox, single amount, top-down) ──────
// Fetches every unpaid challan_item for a family, newest month/year first.
// Feeds the PaymentModal checkbox list directly.
export async function getUnpaidItemsForFamily(schoolId, familyId) {
  const { data, error } = await supabase.rpc('get_unpaid_items', { p_school_id: schoolId });
  if (error) throw error;
  return (data || [])
    .filter(i => i.family_id === familyId)
    .sort((a, b) => (b.year - a.year) || (b.month - a.month));
}

// ─── FEE PAYMENT (matches existing PaymentModal flow, now atomic) ──
// Replaces: savePayment + fire-and-forget(drawAdvance loop, deposit_advance
// rpc, incrementChallanPaid, ) as five separate uncoordinated writes.
// Same inputs your save() already computes — no calculation logic changed,
// just wraps the writes in one Postgres transaction. Also fixes: old-period
// challans' paid_amount now update correctly (previously only the "current"
// challan got incremented, even for selected arrear lines from other challans).
export async function recordFeePayment({
  schoolId, challanId, familyId, paymentDate, paymentMethod,
  receiptNo, referenceNo = null, notes = null, receivedBy,
  netCash, paymentItems, advanceDraws = [],
  overpayStudentId = null, overpayAmount = 0,
}) {
  const { data, error } = await supabase.rpc('record_fee_payment', {
    p_school_id: schoolId,
    p_challan_id: challanId,
    p_family_id: familyId,
    p_payment_date: paymentDate,
    p_payment_method: paymentMethod,
    p_receipt_no: receiptNo,
    p_reference_no: referenceNo,
    p_notes: notes,
    p_received_by: receivedBy,
    p_net_cash: netCash,
    p_payment_items: paymentItems,     // same shape as your existing paymentItemsToInsert
    p_advance_draws: advanceDraws,     // [{ student_id, amount }]
    p_overpay_student_id: overpayStudentId,
    p_overpay_amount: overpayAmount,
  });
  if (error) throw error;
  return data; // new payment id (uuid)
}

// ─── USER DIRECTORY ──────────────────────────────────────
export async function getUsersDirectory(schoolId) {
  const [usersRes, staffRes, familiesRes, studentsRes] = await Promise.all([
    supabase.from('users').select('id, name, email, username, phone, whatsapp, is_active, roles(name, display_name)').eq('school_id', schoolId),
    supabase.from('staff').select('id, name, phone, email, user_id, designations(name)').eq('school_id', schoolId),
    supabase.from('families').select('id, family_id_display, father_name, mother_name, guardian_name, phone').eq('school_id', schoolId),
    supabase.from('students').select('id, name, roll_no, admission_no, user_id, family_id').eq('school_id', schoolId),
  ]);
  if (usersRes.error) throw usersRes.error;
  if (staffRes.error) throw staffRes.error;
  if (familiesRes.error) throw familiesRes.error;
  if (studentsRes.error) throw studentsRes.error;

  const users = usersRes.data || [];
  const byRole = (names) => users.filter(u => names.includes(u.roles?.name));
  const userById = new Map(users.map(u => [u.id, u]));
  const familiesWithStudents = new Set((studentsRes.data || []).map(s => s.family_id));

  // A staff member whose designation is Accountant or Cashier belongs under Management, not Teachers. Their login
  // (if any) is listed there; without a login they appear as a pending row that can generate one, linked to the
  // staff record, with the matching role preselected.
  const mgmtRole = (s) => { const n = s.designations?.name || ''; return /cashier/i.test(n) ? 'cashier' : /accountant/i.test(n) ? 'accountant' : null; };
  const isAccountant = (s) => !!mgmtRole(s);
  const staffRows = staffRes.data || [];
  const management = byRole(['owner', 'director', 'admin', 'accountant', 'cashier']);
  const listed = new Set(management.map(u => u.id));
  for (const s of staffRows.filter(isAccountant)) {
    const linked = s.user_id ? userById.get(s.user_id) : null;
    if (linked) {
      if (!listed.has(linked.id)) { management.push(linked); listed.add(linked.id); }
    } else {
      management.push({
        pendingStaff: true, sourceId: s.id, name: s.name, phone: s.phone, email: s.email,
        linkStaffId: s.id, roles: { name: mgmtRole(s), display_name: mgmtRole(s) === 'cashier' ? 'Cashier' : 'Accountant' },
      });
    }
  }

  return {
    management,
    teachers: staffRows.filter(s => !isAccountant(s)).map(s => ({
      sourceId: s.id, name: s.name, phone: s.phone, email: s.email,
      loginUser: s.user_id ? userById.get(s.user_id) : null,
      linkStaffId: s.id,
    })),
    parents: (familiesRes.data || []).filter(f => familiesWithStudents.has(f.id)).map(f => ({
      sourceId: f.id,
      name: f.father_name || f.guardian_name || f.mother_name || f.family_id_display,
      phone: f.phone, familyIdDisplay: f.family_id_display,
      loginUser: users.find(u => u.username?.endsWith('.' + (f.family_id_display || '').toLowerCase().replace(/[^a-z0-9]/gi, ''))) || null,
      linkFamilyId: f.id,
    })),
    students: (studentsRes.data || []).map(s => ({
      sourceId: s.id, name: s.name, phone: null, email: null,
      admissionNo: s.admission_no || null,
      loginUser: s.user_id ? userById.get(s.user_id) : null,
      linkStudentId: s.id,
    })),
  };
}

export async function bulkCreateUsers(schoolId, roleName, records, buildPayload) {
  const results = [];
  for (const record of records) {
    try {
      const payload = buildPayload(record);
      const res = await createUser({ ...payload, roleName, schoolId });
      results.push({ ok: true, record, res });
    } catch (err) {
      results.push({ ok: false, record, error: err.message });
    }
  }
  return results;
}

export async function resetUserPassword(targetUserId, newPassword) {
  return edgeFunction('reset-password', { targetUserId, newPassword });
}

export async function setUserActive(targetUserId, active) {
  return edgeFunction('set-user-active', { targetUserId, active });
}

// Name, phone and WhatsApp only. The RPC keeps the stored phone/WhatsApp when a blank is
// sent, and role, school, status, email and username cannot be changed through it.
export async function updateUserProfile(userId, { name, phone, whatsapp }) {
  const { data, error } = await supabase.rpc('admin_update_user', { p_user_id: userId, p_name: name, p_phone: phone || null, p_whatsapp: whatsapp || null });
  if (error) throw error;
  return data;
}

// Which user ids are platform (super) admins. The RPC returns a set of uuids; the raw shape is
// logged once so a different return shape is easy to spot.
let platformAdminShapeLogged = false;
export async function getPlatformAdminIds() {
  const { data, error } = await supabase.rpc('platform_admin_ids');
  if (error) throw error;
  if (!platformAdminShapeLogged) { platformAdminShapeLogged = true; console.info('platform_admin_ids raw result:', data); }
  return new Set((data || []).map(r => (typeof r === 'string' ? r : (r.platform_admin_ids ?? r.id))));
}

export async function markPasswordChanged(userId) {
  const { error } = await supabase.from('users').update({ password_changed: true }).eq('id', userId);
  if (error) throw error;
}

// ─── COMPLAINTS ────────────────────────────────────────────
export async function createComplaint(schoolId, familyId, studentId, userId, { category, title, message }) {
  const { data, error } = await supabase.from('complaints').insert({
    school_id: schoolId, family_id: familyId, student_id: studentId || null,
    created_by: userId, category, title, message,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function getFamilyComplaints(familyId) {
  const { data, error } = await supabase.from('complaints')
    .select('*').eq('family_id', familyId).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getSchoolComplaints(schoolId, statusFilter) {
  let q = supabase.from('complaints')
    .select('*, families(family_id_display), students(name)')
    .eq('school_id', schoolId);
  if (statusFilter) q = q.eq('status', statusFilter);
  const { data, error } = await q.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function resolveComplaint(complaintId, resolution, resolvedByUserId) {
  const { data: complaint, error: fetchError } = await supabase
    .from('complaints').select('school_id').eq('id', complaintId).single();
  if (fetchError) throw fetchError;

  const { error } = await supabase.from('complaints').update({
    status: 'resolved', resolution, resolved_by: resolvedByUserId, resolved_at: new Date().toISOString(),
  }).eq('id', complaintId);
  if (error) throw error;

  const { error: logError } = await supabase
    .from('complaint_updates')
    .insert({ complaint_id: complaintId, school_id: complaint.school_id, status: 'resolved', note: resolution, updated_by: resolvedByUserId });
  if (logError) throw logError;
}

// ─── EXPENSES ────────────────────────────────────────────────────────────────

export async function getExpenseCategories(schoolId) {
  const { data, error } = await supabase
    .from('expense_categories')
    .select('id, name, tone, display_order, heads:expense_heads(id, name, is_active)')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('display_order');
  if (error) throw error;
  return (data || []).map(c => ({
    ...c,
    heads: (c.heads || []).filter(h => h.is_active),
  }));
}

// Raw rows over the window are fetched and counted client-side (no DB aggregate function
// for this) — fine at the per-school expense volumes this covers, even over a few months.
export async function getTopExpenseCombos(schoolId, sinceISO, limit = 20) {
  const { data, error } = await supabase
    .from('expense_entries')
    .select('category_id, head_id')
    .eq('school_id', schoolId)
    .gte('expense_date', sinceISO)
    .not('head_id', 'is', null)
    .limit(5000);
  if (error) throw error;
  const counts = new Map();
  for (const row of data || []) {
    const key = `${row.category_id}:${row.head_id}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([key, count]) => {
      const [categoryId, headId] = key.split(':');
      return { categoryId, headId, count };
    });
}

export async function getExpenseApprovalSetting(schoolId) {
  const { data, error } = await supabase
    .from('approval_settings')
    .select('requires_approval')
    .eq('school_id', schoolId)
    .eq('module', 'expenses')
    .maybeSingle();
  if (error) throw error;
  return data ? !!data.requires_approval : true;
}

// lines: [{ categoryId, headId, amount, paymentMethod, note }]
export async function submitExpenseVoucher(schoolId, voucherDateISO, lines, createdBy) {
  const payload = lines.map(l => ({
    category_id: l.categoryId,
    head_id: l.headId,
    amount: Number(l.amount),
    payment_method: l.paymentMethod || 'Cash',
    note: l.note || '',
  }));
  const { data, error } = await supabase.rpc('submit_expense_voucher', {
    p_school_id: schoolId,
    p_voucher_date: voucherDateISO,
    p_lines: payload,
    p_created_by: createdBy,
  });
  if (error) throw error;
  return {
    voucherNo: data.voucher_no,
    entryIds: data.entry_ids,
    total: Number(data.total),
    status: data.status,
  };
}

// ─── EXPENSE CATEGORIES/HEADS ADMIN ──────────────────────
export async function addExpenseCategory(schoolId, name, tone) {
  const { data, error } = await supabase
    .from('expense_categories')
    .insert({ school_id: schoolId, name: name.trim(), tone: tone || 'blue' })
    .select('id, name, tone').single();
  if (error) throw error;
  return data;
}

export async function deactivateExpenseCategory(categoryId) {
  const { count } = await supabase
    .from('expense_entries')
    .select('id', { count: 'exact', head: true })
    .eq('category_id', categoryId);
  if (count > 0) {
    throw new Error(`Cannot deactivate — ${count} expense entr${count === 1 ? 'y' : 'ies'} use this category.`);
  }
  const { error } = await supabase.from('expense_categories').update({ is_active: false }).eq('id', categoryId);
  if (error) throw error;
}

export async function addExpenseHead(categoryId, name) {
  const { data, error } = await supabase
    .from('expense_heads')
    .insert({ category_id: categoryId, name: name.trim() })
    .select('id, name').single();
  if (error) throw error;
  return data;
}

export async function deactivateExpenseHead(headId) {
  const { count } = await supabase
    .from('expense_entries')
    .select('id', { count: 'exact', head: true })
    .eq('head_id', headId);
  if (count > 0) {
    throw new Error(`Cannot deactivate — ${count} expense entr${count === 1 ? 'y' : 'ies'} use this sub-head.`);
  }
  const { error } = await supabase.from('expense_heads').update({ is_active: false }).eq('id', headId);
  if (error) throw error;
}

// ─── EXPENSE BUDGETS ──────────────────────────────────────
export async function getExpenseBudgets(schoolId, fiscalYear) {
  const { data, error } = await supabase
    .from('expense_budgets')
    .select('id, category_id, head_id, monthly_amount, status')
    .eq('school_id', schoolId)
    .eq('fiscal_year', fiscalYear);
  if (error) throw error;
  return data || [];
}

export async function saveExpenseBudgetLine(schoolId, fiscalYear, categoryId, headId, monthlyAmount) {
  const { error } = await supabase.from('expense_budgets').upsert(
    {
      school_id: schoolId, fiscal_year: fiscalYear, category_id: categoryId, head_id: headId,
      monthly_amount: Number(monthlyAmount) || 0, updated_at: new Date().toISOString(),
    },
    { onConflict: 'fiscal_year,head_id' }
  );
  if (error) throw error;
}

// ─── RECURRING EXPENSES ───────────────────────────────────
export async function getRecurringExpenses(schoolId) {
  const { data, error } = await supabase
    .from('recurring_expenses')
    .select('id, title, category_id, head_id, frequency, due_day, fixed_amount, is_variable, last_paid_amount, last_paid_date, expense_categories(name), expense_heads(name)')
    .eq('school_id', schoolId)
    .eq('is_active', true)
    .order('title');
  if (error) throw error;
  return data || [];
}

export async function saveRecurringExpense(schoolId, recurring) {
  const row = {
    school_id: schoolId, title: recurring.title, category_id: recurring.categoryId,
    head_id: recurring.headId, frequency: recurring.frequency || 'monthly',
    due_day: Number(recurring.dueDay) || 1,
    fixed_amount: recurring.isVariable ? null : Number(recurring.fixedAmount || 0),
    is_variable: !!recurring.isVariable,
  };
  if (recurring.id) {
    const { error } = await supabase.from('recurring_expenses').update(row).eq('id', recurring.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('recurring_expenses').insert(row);
    if (error) throw error;
  }
}

export async function deactivateRecurringExpense(id) {
  const { error } = await supabase.from('recurring_expenses').update({ is_active: false }).eq('id', id);
  if (error) throw error;
}

export async function markRecurringPaid(id, amount, dateISO) {
  const { error } = await supabase
    .from('recurring_expenses')
    .update({ last_paid_amount: Number(amount), last_paid_date: dateISO })
    .eq('id', id);
  if (error) throw error;
}

export async function getInactiveExpenseCategories(schoolId) {
  const { data, error } = await supabase
    .from('expense_categories')
    .select('id, name, tone')
    .eq('school_id', schoolId)
    .eq('is_active', false)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function reactivateExpenseCategory(id) {
  const { error } = await supabase.from('expense_categories').update({ is_active: true }).eq('id', id);
  if (error) throw error;
}

export async function getInactiveExpenseHeads(schoolId) {
  const { data, error } = await supabase
    .from('expense_heads')
    .select('id, name, category_id, expense_categories!inner(name, school_id)')
    .eq('expense_categories.school_id', schoolId)
    .eq('is_active', false)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function reactivateExpenseHead(id) {
  const { error } = await supabase.from('expense_heads').update({ is_active: true }).eq('id', id);
  if (error) throw error;
}

export async function getExpenseLedger(schoolId, { from, to, status } = {}) {
  let q = supabase
    .from('expense_entries')
    .select('id, entry_no, voucher_no, title, payee, amount, expense_date, payment_method, status, notes, category_id, head_id, expense_categories(name, tone), expense_heads(name)')
    .eq('school_id', schoolId)
    .order('expense_date', { ascending: false })
    .order('entry_no', { ascending: false });
  if (from) q = q.gte('expense_date', from);
  if (to) q = q.lte('expense_date', to);
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function payExpenseVoucher(schoolId, voucherNo, paidBy) {
  const { data, error } = await supabase.rpc('pay_expense_voucher', {
    p_school_id: schoolId, p_voucher_no: voucherNo, p_paid_by: paidBy,
  });
  if (error) throw error;
  return { paidCount: data.paid_count, total: Number(data.total) };
}

export async function payExpenseEntry(entryId, paidBy) {
  const { data, error } = await supabase.rpc('record_expense_payment', {
    p_entry_id: entryId, p_paid_by: paidBy,
  });
  if (error) throw error;
  return data;
}

export async function reverseExpensePayment(entryId, reason, userId) {
  const { data, error } = await supabase.rpc('reverse_expense_payment', {
    p_entry_id: entryId, p_reason: reason, p_user_id: userId,
  });
  if (error) throw error;
  return data;
}

export async function approveExpenseVoucher(schoolId, voucherNo, approvedBy) {
  const { data, error } = await supabase.rpc('approve_expense_voucher', {
    p_school_id: schoolId, p_voucher_no: voucherNo, p_approved_by: approvedBy,
  });
  if (error) throw error;
  return { approvedCount: data.approved_count };
}

export async function deleteExpenseEntry(entryId) {
  const { data, error } = await supabase.rpc('delete_expense_entry', { p_entry_id: entryId });
  if (error) throw error;
  return data;
}

export async function deleteExpenseVoucher(schoolId, voucherNo) {
  const { data, error } = await supabase.rpc('delete_expense_voucher', {
    p_school_id: schoolId, p_voucher_no: voucherNo,
  });
  if (error) throw error;
  return { deletedCount: data.deleted_count };
}

export async function reportExpenseSummary(schoolId, from, to, fiscalYear = null) {
  const { data, error } = await supabase.rpc('report_expense_summary', {
    p_school_id: schoolId, p_from: from, p_to: to, p_fiscal_year: fiscalYear,
  });
  if (error) throw error;
  return data || [];
}

export async function reportExpenseMonthly(schoolId, year) {
  const { data, error } = await supabase.rpc('report_expense_monthly', {
    p_school_id: schoolId, p_year: year,
  });
  if (error) throw error;
  return data || [];
}

export async function updateExpenseEntry(entryId, { categoryId, headId, amount, paymentMethod, expenseDate, note }, userId) {
  const { data, error } = await supabase.rpc('update_expense_entry', {
    p_entry_id: entryId,
    p_category_id: categoryId,
    p_head_id: headId,
    p_amount: Number(amount),
    p_payment_method: paymentMethod || 'Cash',
    p_expense_date: expenseDate,
    p_note: note || '',
    p_user_id: userId,
  });
  if (error) throw error;
  return data;
}

export async function financePosition(schoolId) {
  const { data, error } = await supabase.rpc('finance_position', { p_school_id: schoolId });
  if (error) throw error;
  return data || [];
}

export async function financeCashBook(schoolId, date) {
  const { data, error } = await supabase.rpc('finance_cash_book', { p_school_id: schoolId, p_date: date });
  if (error) throw error;
  return data || [];
}

export async function financeFlowMonthly(schoolId, year) {
  const { data, error } = await supabase.rpc('finance_flow_monthly', {
    p_school_id: schoolId, p_year: year,
  });
  if (error) throw error;
  return data || [];
}

export async function financeStatement(schoolId, from, to) {
  const { data, error } = await supabase.rpc('finance_statement', {
    p_school_id: schoolId, p_from: from, p_to: to,
  });
  if (error) throw error;
  return data || [];
}

export async function getFinanceSources(schoolId) {
  const { data, error } = await supabase
    .from('finance_sources')
    .select('id, kind, name, counterparty, principal, started_on, notes')
    .eq('school_id', schoolId).eq('is_active', true).order('name');
  if (error) throw error;
  return data || [];
}

export async function saveFinanceSource(schoolId, source) {
  const row = {
    school_id: schoolId, kind: source.kind, name: source.name,
    counterparty: source.counterparty || null,
    principal: Number(source.principal) || 0,
    started_on: source.startedOn || null, notes: source.notes || null,
  };
  if (source.id) {
    const { error } = await supabase.from('finance_sources').update(row).eq('id', source.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('finance_sources').insert(row);
    if (error) throw error;
  }
}

export async function getFinanceMovements(schoolId) {
  const { data, error } = await supabase
    .from('finance_movements')
    .select('id, movement_type, amount, movement_date, payment_method, notes, finance_sources(name, kind)')
    .eq('school_id', schoolId)
    .order('movement_date', { ascending: false })
    .limit(100);
  if (error) throw error;
  return data || [];
}

export async function recordFinanceMovement(schoolId, { sourceId, movementType, amount, date, paymentMethod, notes }, userId) {
  const { data, error } = await supabase.rpc('record_finance_movement', {
    p_school_id: schoolId,
    p_source_id: sourceId || null,
    p_movement_type: movementType,
    p_amount: Number(amount),
    p_date: date,
    p_payment_method: paymentMethod || 'Cash',
    p_notes: notes || '',
    p_user_id: userId,
  });
  if (error) throw error;
  return { movementId: data.movement_id, entryNo: data.entry_no };
}

export async function postOpeningBalance(schoolId, accountCode, amount, asOf, userId) {
  const { data, error } = await supabase.rpc('post_opening_balance', {
    p_school_id: schoolId,
    p_account_code: accountCode,
    p_amount: Number(amount),
    p_as_of: asOf,
    p_user_id: userId,
  });
  if (error) throw error;
  return data;
}

// Books the value of opening stock created by one product import (Dr Inventory / Cr Opening Balance Equity).
// importId identifies the import, so posting the same import again must not double-book it.
export async function postOpeningStock(schoolId, importId, amount, asOf, userId) {
  const { data, error } = await supabase.rpc('post_opening_stock', {
    p_school_id: schoolId,
    p_import_id: importId,
    p_amount: Number(amount),
    p_as_of: asOf,
    p_user_id: userId,
  });
  if (error) throw error;
  return data;
}

// ─── HOLIDAYS ────────────────────────────────────────────
export async function getHolidays(schoolId) {
  const { data, error } = await supabase
    .from('holidays')
    .select('*')
    .eq('school_id', schoolId)
    .order('start_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Returns holidays whose date range overlaps [start, end] (ISO date strings)
export async function getHolidaysInRange(schoolId, start, end) {
  const { data, error } = await supabase
    .from('holidays')
    .select('id, title, category, start_date, end_date, repeats_annually')
    .eq('school_id', schoolId)
    .lte('start_date', end)
    .gte('end_date', start);
  if (error) throw error;
  return data || [];
}

export async function upsertHoliday(schoolId, userId, row) {
  const payload = {
    school_id: schoolId,
    created_by: userId,
    title: row.title,
    category: row.category,
    start_date: row.start_date,
    end_date: row.end_date,
    repeats_annually: !!row.repeats_annually,
  };
  if (row.id) {
    const { error } = await supabase.from('holidays').update(payload).eq('id', row.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('holidays').insert(payload);
    if (error) throw error;
  }
}

export async function deleteHoliday(id) {
  const { error } = await supabase.from('holidays').delete().eq('id', id);
  if (error) throw error;
}

export async function getSchoolEvents(schoolId) {
  const { data, error } = await supabase.from('school_events')
    .select('*').eq('school_id', schoolId).order('start_date');
  if (error) throw error;
  return data || [];
}

export async function upsertSchoolEvent(schoolId, userId, row) {
  const payload = {
    school_id: schoolId,
    created_by: userId,
    title: row.title,
    description: row.description || null,
    category: row.category,
    start_date: row.start_date,
    end_date: row.end_date || row.start_date,
  };
  if (row.id) {
    const { error } = await supabase.from('school_events').update(payload).eq('id', row.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('school_events').insert(payload);
    if (error) throw error;
  }
}

export async function deleteSchoolEvent(id) {
  const { error } = await supabase.from('school_events').delete().eq('id', id);
  if (error) throw error;
}

export async function getStudentsMissingFeeAssignments(schoolId) {
  const { data, error } = await supabase
    .from('students')
    .select('id, name, admission_no, grade_id, classes(name)')
    .eq('school_id', schoolId)
    .eq('status', 'active');
  if (error) throw error;
  const { data: assigned } = await supabase
    .from('student_fee_assignments')
    .select('student_id')
    .eq('is_active', true)
    .in('student_id', (data || []).map(s => s.id));
  const assignedIds = new Set((assigned || []).map(a => a.student_id));
  return (data || []).filter(s => !assignedIds.has(s.id));
}

export async function applyFeeStructureToStudents(schoolId, studentIds, userId) {
  let studentsUpdated = 0, assignmentsCreated = 0, skippedNoStructure = 0;
  const errors = [];

  const { data: students } = await supabase
    .from('students').select('id, grade_id').in('id', studentIds);

  const gradeIds = [...new Set((students || []).map(s => s.grade_id).filter(Boolean))];
  const { data: structures } = await supabase
    .from('fee_structures').select('id, class_id')
    .eq('school_id', schoolId).eq('is_active', true).in('class_id', gradeIds);
  const structureByGrade = new Map((structures || []).map(fs => [fs.class_id, fs.id]));

  const structureIds = [...structureByGrade.values()];
  const { data: items } = await supabase
    .from('fee_structure_items').select('fee_structure_id, fee_head_id, amount')
    .in('fee_structure_id', structureIds);
  const itemsByStructure = new Map();
  (items || []).forEach(it => {
    if (!itemsByStructure.has(it.fee_structure_id)) itemsByStructure.set(it.fee_structure_id, []);
    itemsByStructure.get(it.fee_structure_id).push(it);
  });

  const { data: existing } = await supabase
    .from('student_fee_assignments').select('student_id, fee_head_id')
    .eq('is_active', true).in('student_id', studentIds);
  const existingSet = new Set((existing || []).map(e => `${e.student_id}:${e.fee_head_id}`));

  const rowsToInsert = [];
  const studentsWithNewRows = new Set();

  for (const student of (students || [])) {
    const structureId = structureByGrade.get(student.grade_id);
    if (!structureId) { skippedNoStructure++; continue; }
    const lineItems = itemsByStructure.get(structureId) || [];
    for (const item of lineItems) {
      const key = `${student.id}:${item.fee_head_id}`;
      if (existingSet.has(key)) continue;
      rowsToInsert.push({
        school_id: schoolId, student_id: student.id, fee_head_id: item.fee_head_id,
        amount: item.amount, effective_from: todayISO(),
        is_active: true, created_by: userId,
      });
      studentsWithNewRows.add(student.id);
    }
  }

  const CHUNK = 500;
  for (let i = 0; i < rowsToInsert.length; i += CHUNK) {
    const chunk = rowsToInsert.slice(i, i + CHUNK);
    const { error } = await supabase.from('student_fee_assignments').insert(chunk);
    if (error) errors.push({ chunk: i / CHUNK, error: error.message });
    else assignmentsCreated += chunk.length;
  }
  studentsUpdated = studentsWithNewRows.size;
  return { studentsUpdated, assignmentsCreated, skippedNoStructure, errors };
}

// ─── STUDENT AFFAIRS & BEHAVIOUR ─────────────────────────
const BEHAVIOR_INCIDENT_SELECT =
  '*, students(name, admission_no, roll_no), sections(id, name, classes(name)), reporter:users!reported_by(name), behavior_ratings(rating, behavior_indicators(name))';

function mapBehaviorSection(row, isClassTeacher) {
  const sec = row.sections || row;
  const cls = sec.classes || {};
  return {
    sectionId: sec.id,
    name: `${cls.name || ''} · ${sec.name || ''}`.trim(),
    orderNum: cls.order_num ?? 999,
    isClassTeacher: !!isClassTeacher,
  };
}

function sortBehaviorSections(list) {
  return list.sort((a, b) => a.orderNum - b.orderNum || a.name.localeCompare(b.name));
}

// Latest incidents school-wide (or only the ones a user reported). Capped: PostgREST truncates at 1000.
export async function getBehaviorIncidents(schoolId, { reportedBy = null, limit = 300 } = {}) {
  let q = supabase.from('behavior_incidents').select(BEHAVIOR_INCIDENT_SELECT).eq('school_id', schoolId);
  if (reportedBy) q = q.eq('reported_by', reportedBy);
  const { data, error } = await q
    .order('occurred_on', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function createBehaviorIncident(schoolId, userId, f) {
  const { data, error } = await supabase.from('behavior_incidents').insert({
    school_id: schoolId,
    reported_by: userId,
    student_id: f.studentId,
    occurred_on: f.occurredOn,
    context: (f.context || '').trim(),
    behavior: (f.behavior || '').trim(),
    action_taken: (f.actionTaken || '').trim() || null,
    student_response: (f.studentResponse || '').trim() || null,
    outcome: (f.outcome || '').trim() || null,
  }).select(BEHAVIOR_INCIDENT_SELECT).single();
  if (error) throw error;
  return data;
}

// Student, section and reporter are locked by a database trigger; only these fields can change.
export async function updateBehaviorIncident(id, f) {
  const { data, error } = await supabase.from('behavior_incidents').update({
    occurred_on: f.occurredOn,
    context: (f.context || '').trim(),
    behavior: (f.behavior || '').trim(),
    action_taken: (f.actionTaken || '').trim() || null,
    student_response: (f.studentResponse || '').trim() || null,
    outcome: (f.outcome || '').trim() || null,
  }).eq('id', id).select(BEHAVIOR_INCIDENT_SELECT).single();
  if (error) throw error;
  return data;
}

export async function deleteBehaviorIncident(id) {
  const { error } = await supabase.from('behavior_incidents').delete().eq('id', id);
  if (error) {
    if (error.code === '23503') throw new Error('This incident is linked to a rating and cannot be deleted');
    throw error;
  }
}

// Sections the logged-in teacher is assigned to (any subject), one entry per section.
export async function getMyBehaviorSections(schoolId, userId) {
  const yearId = await getCurrentAcademicYear(schoolId);
  if (!yearId) return [];
  const { data, error } = await supabase.from('teacher_assignments')
    .select('section_id, is_class_teacher, sections(id, name, classes(name, order_num)), staff!inner(user_id)')
    .eq('school_id', schoolId)
    .eq('staff.user_id', userId)
    .eq('academic_year_id', yearId);
  if (error) throw error;
  const map = new Map();
  (data || []).forEach(r => {
    if (!r.sections) return;
    const prev = map.get(r.section_id);
    map.set(r.section_id, mapBehaviorSection(r, (prev && prev.isClassTeacher) || r.is_class_teacher));
  });
  return sortBehaviorSections([...map.values()]);
}

// Every active section, for reviewers (admin).
export async function getAllBehaviorSections(schoolId) {
  const { data, error } = await supabase.from('sections')
    .select('id, name, classes(name, order_num)')
    .eq('school_id', schoolId)
    .eq('is_active', true);
  if (error) throw error;
  return sortBehaviorSections((data || []).map(r => mapBehaviorSection(r, false)));
}

export async function getSectionStudentsForBehavior(sectionId) {
  const { data, error } = await supabase.from('students')
    .select('id, name, admission_no, roll_no')
    .eq('section_id', sectionId)
    .eq('status', 'active')
    .order('name');
  if (error) throw error;
  return data || [];
}


// ─── BEHAVIOUR CYCLES, FLAGS, SNAPSHOTS ───────────────────
const BEHAVIOR_PAGE = 1000;

function behaviorLocalDate() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// PostgREST truncates at 1,000 rows. buildQuery must return a query with a
// deterministic order so pages do not overlap.
async function fetchAllBehaviorPages(buildQuery) {
  const rows = [];
  for (let from = 0; ; from += BEHAVIOR_PAGE) {
    const { data, error } = await buildQuery().range(from, from + BEHAVIOR_PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < BEHAVIOR_PAGE) break;
  }
  return rows;
}

function mapBehaviorCycleError(error) {
  if (error?.code === '23505') return new Error('Another cycle is already open. Close it first.');
  if (error?.code === '23514') return new Error('The deadline cannot be before the start date.');
  return error;
}

export async function getBehaviorCycles(schoolId) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .select('*')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// start_date is required by the table; it is reset to the day the cycle opens.
export async function createBehaviorCycle(schoolId, { name, endDate }) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .insert({ school_id: schoolId, name: name.trim(), start_date: behaviorLocalDate(), end_date: endDate, status: 'draft' })
    .select()
    .single();
  if (error) throw mapBehaviorCycleError(error);
  return data;
}

export async function updateBehaviorCycleDraft(id, { name, endDate }) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .update({ name: name.trim(), end_date: endDate })
    .eq('id', id)
    .eq('status', 'draft')
    .select();
  if (error) throw mapBehaviorCycleError(error);
  if (!data || data.length === 0) throw new Error('Only a draft cycle can be edited.');
  return data[0];
}

export async function openBehaviorCycle(id) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .update({ status: 'open', start_date: behaviorLocalDate() })
    .eq('id', id)
    .eq('status', 'draft')
    .select();
  if (error) {
    if (error.code === '23514') throw new Error('The deadline is before today. Change the deadline first.');
    throw mapBehaviorCycleError(error);
  }
  if (!data || data.length === 0) throw new Error('Only a draft cycle can be opened.');
  return data[0];
}

export async function setBehaviorCycleDeadline(id, endDate) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .update({ end_date: endDate })
    .eq('id', id)
    .eq('status', 'open')
    .select();
  if (error) throw mapBehaviorCycleError(error);
  if (!data || data.length === 0) throw new Error('Only an open cycle can be extended.');
  return data[0];
}

export async function finalizeBehaviorCycle(id) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .update({ status: 'closed' })
    .eq('id', id)
    .eq('status', 'open')
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('This cycle is not open.');
  return data[0];
}

export async function getBehaviorIndicators() {
  const { data, error } = await supabase
    .from('behavior_indicators')
    .select('id, code, name, sort_order')
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

// One row per rating on a SUBMITTED assessment of the cycle.
export async function getBehaviorCycleRatingRows(cycleId) {
  return fetchAllBehaviorPages(() =>
    supabase
      .from('behavior_ratings')
      .select('assessment_id, indicator_id, rating, incident_id, behavior_assessments!inner(student_id, rater_staff_id, rater_role, cycle_id, status)')
      .eq('behavior_assessments.cycle_id', cycleId)
      .eq('behavior_assessments.status', 'submitted')
      .order('assessment_id')
      .order('indicator_id')
  );
}

// Snapshots of the most recent earlier closed cycle that has any.
export async function getPreviousBehaviorSnapshots(schoolId, cycleId) {
  const { data: cycles, error } = await supabase
    .from('behavior_cycles')
    .select('id')
    .eq('school_id', schoolId)
    .eq('status', 'closed')
    .neq('id', cycleId)
    .order('closed_at', { ascending: false })
    .limit(5);
  if (error) throw error;
  for (const c of cycles || []) {
    const rows = await fetchAllBehaviorPages(() =>
      supabase
        .from('behavior_snapshots')
        .select('student_id, indicator_id, score')
        .eq('cycle_id', c.id)
        .order('student_id')
        .order('indicator_id')
    );
    if (rows.length) return rows;
  }
  return [];
}

export async function countBehaviorFlags(cycleId) {
  const { count, error } = await supabase
    .from('behavior_flags')
    .select('id', { count: 'exact', head: true })
    .eq('cycle_id', cycleId);
  if (error) throw error;
  return count || 0;
}

export async function insertBehaviorFlags(rows) {
  for (let i = 0; i < rows.length; i += 500) {
    const { error } = await supabase.from('behavior_flags').insert(rows.slice(i, i + 500));
    if (error) throw error;
  }
}

export async function upsertBehaviorSnapshots(rows) {
  for (let i = 0; i < rows.length; i += 500) {
    const { error } = await supabase
      .from('behavior_snapshots')
      .upsert(rows.slice(i, i + 500), { onConflict: 'cycle_id,student_id,indicator_id' });
    if (error) throw error;
  }
}

export async function getBehaviorFlags(cycleId) {
  return fetchAllBehaviorPages(() =>
    supabase
      .from('behavior_flags')
      .select('*, students(name, admission_no), behavior_indicators(name), staff(name)')
      .eq('cycle_id', cycleId)
      .order('created_at')
      .order('id')
  );
}

export async function getBehaviorStaffNames(schoolId) {
  const { data, error } = await supabase.from('staff').select('id, name').eq('school_id', schoolId);
  if (error) throw error;
  const map = {};
  (data || []).forEach(s => { map[s.id] = s.name; });
  return map;
}

export async function reviewBehaviorFlag(id, userId, status, note) {
  const text = (note || '').trim();
  if (status === 'dismissed' && !text) throw new Error('Add a note before dismissing.');
  const { data, error } = await supabase
    .from('behavior_flags')
    .update({ status, reviewed_by: userId, reviewed_at: new Date().toISOString(), review_note: text || null })
    .eq('id', id)
    .eq('status', 'open')
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('This flag was already reviewed.');
  return data[0];
}

// Expected = active students in each section the teacher is assigned to
// (a section counts once even with several subjects). Submitted = submitted
// assessments by that teacher. Admins cannot see other teachers' drafts, so
// "not submitted" covers both not started and still in draft.
export async function getBehaviorCycleProgress(schoolId, cycleId) {
  const { data: assignments, error: aErr } = await supabase
    .from('teacher_assignments')
    .select('teacher_id, section_id, staff!inner(name, status), sections!inner(name, is_active, classes(name))')
    .eq('school_id', schoolId)
    .eq('staff.status', 'Active')
    .eq('sections.is_active', true)
    .eq('academic_year_id', await getCurrentAcademicYear(schoolId));
  if (aErr) throw aErr;

  const students = await fetchAllBehaviorPages(() =>
    supabase
      .from('students')
      .select('id, section_id')
      .eq('school_id', schoolId)
      .eq('status', 'active')
      .not('section_id', 'is', null)
      .order('id')
  );
  const perSection = {};
  students.forEach(s => { perSection[s.section_id] = (perSection[s.section_id] || 0) + 1; });

  const submittedRows = await fetchAllBehaviorPages(() =>
    supabase
      .from('behavior_assessments')
      .select('id, rater_staff_id')
      .eq('cycle_id', cycleId)
      .eq('status', 'submitted')
      .order('id')
  );
  const submittedBy = {};
  submittedRows.forEach(r => { submittedBy[r.rater_staff_id] = (submittedBy[r.rater_staff_id] || 0) + 1; });

  const teachers = {};
  (assignments || []).forEach(a => {
    const t = teachers[a.teacher_id] || (teachers[a.teacher_id] = { teacherId: a.teacher_id, name: a.staff?.name || 'Unknown', sections: {} });
    const cls = a.sections?.classes?.name;
    t.sections[a.section_id] = cls ? `${cls}-${a.sections.name}` : a.sections?.name || '';
  });
  const rows = Object.values(teachers).map(t => {
    const ids = Object.keys(t.sections);
    return {
      teacherId: t.teacherId,
      name: t.name,
      sections: ids.map(id => t.sections[id]).sort(),
      expected: ids.reduce((sum, id) => sum + (perSection[id] || 0), 0),
      submitted: submittedBy[t.teacherId] || 0,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  return {
    rows,
    expected: rows.reduce((s, r) => s + r.expected, 0),
    submitted: submittedRows.length,
  };
}


// ─── BEHAVIOUR ASSESSMENT: TEACHER SIDE ───────────────────
export async function getOpenBehaviorCycle(schoolId) {
  const { data, error } = await supabase
    .from('behavior_cycles')
    .select('*')
    .eq('school_id', schoolId)
    .eq('status', 'open')
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function getBehaviorRubric() {
  const { data, error } = await supabase
    .from('behavior_rubric')
    .select('indicator_id, level, descriptor')
    .order('indicator_id')
    .order('level');
  if (error) throw error;
  return data || [];
}

// The caller's own assessments for a cycle (drafts and submitted), with ratings.
export async function getMyBehaviorAssessments(cycleId, staffId) {
  return fetchAllBehaviorPages(() =>
    supabase
      .from('behavior_assessments')
      .select('id, student_id, section_id, status, behavior_ratings(indicator_id, rating, incident_id)')
      .eq('cycle_id', cycleId)
      .eq('rater_staff_id', staffId)
      .order('id')
  );
}

// Creates the draft assessment on first save, then upserts the ratings.
// Returns the assessment id.
export async function saveBehaviorDraft({ schoolId, cycleId, staffId, studentId, sectionId, assessmentId, rows }) {
  let id = assessmentId;
  if (!id) {
    const { data, error } = await supabase
      .from('behavior_assessments')
      .insert({ school_id: schoolId, cycle_id: cycleId, student_id: studentId, section_id: sectionId, rater_staff_id: staffId })
      .select('id')
      .single();
    if (error && error.code === '23505') {
      const { data: existing, error: e2 } = await supabase
        .from('behavior_assessments')
        .select('id')
        .eq('cycle_id', cycleId)
        .eq('student_id', studentId)
        .eq('rater_staff_id', staffId)
        .single();
      if (e2) throw e2;
      id = existing.id;
    } else if (error) {
      throw error;
    } else {
      id = data.id;
    }
  }
  if (rows && rows.length) {
    const payload = rows.map(r => ({ ...r, assessment_id: id }));
    const { error } = await supabase
      .from('behavior_ratings')
      .upsert(payload, { onConflict: 'assessment_id,indicator_id' });
    if (error) throw error;
  }
  return id;
}

// Submits drafts one by one through the RPC (it checks completeness, evidence
// and the deadline). Returns { ok: [ids], failed: [{ id, message }] }.
export async function submitBehaviorAssessments(ids) {
  const ok = [];
  const failed = [];
  for (let i = 0; i < ids.length; i += 5) {
    const batch = ids.slice(i, i + 5);
    const results = await Promise.allSettled(
      batch.map(id => supabase.rpc('submit_behavior_assessment', { p_assessment_id: id }).then(({ error }) => { if (error) throw error; }))
    );
    results.forEach((r, idx) => {
      if (r.status === 'fulfilled') ok.push(batch[idx]);
      else failed.push({ id: batch[idx], message: r.reason?.message || 'Failed to submit' });
    });
  }
  return { ok, failed };
}

export async function getStudentBehaviorIncidents(studentId) {
  const { data, error } = await supabase
    .from('behavior_incidents')
    .select('id, occurred_on, behavior, context, reporter:users!reported_by(name)')
    .eq('student_id', studentId)
    .order('occurred_on', { ascending: false })
    .limit(30);
  if (error) throw error;
  return data || [];
}

// Active students with their family and class, for PTC slips. Paged because a school can pass 1000 rows.
export async function getPtcStudents(schoolId) {
  const rows = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from('students')
      .select('id, name, family_id, classes:grade_id(name, order_num), sections(name), families(id, family_id_display, father_name, mother_name, guardian_name, phone, mother_phone)')
      .eq('school_id', schoolId).eq('status', 'active')
      .order('id').range(from, from + 999);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return rows;
}

// Pay one staff member's month in a single database call: marks the school and academy parts paid, posts the
// journals, recovers advances and loans and refreshes the payroll totals together (all or nothing).
// adjustment is one signed amount: positive is a prize added to the salary, negative a fine taken off.
export async function payStaffMonthAdjusted(schoolItem, academyItem, { adjustment = 0, note = '', method = 'cash', paid = null } = {}, userId) {
  const { error } = await supabase.rpc('pay_staff_month', {
    p_school_item: schoolItem && !schoolItem.payment_date ? schoolItem.id : null,
    p_academy_item: academyItem && !academyItem.payment_date ? academyItem.id : null,
    p_method: method, p_user: userId, p_date: todayISO(),
    p_adjustment: Math.round(Number(adjustment || 0)), p_note: note || null,
    p_paid: paid === null || paid === undefined ? null : Math.round(Number(paid)),
  });
  if (error) throw error;
}

// Attendance summaries for many staff in one round trip.
// items: [{ staff_id, source: 'school' | 'academy', gross }] -> { 'school:<id>': summary, 'academy:<id>': summary }
export async function getPayrollAttendanceSummaries(month, year, items) {
  const { data, error } = await supabase.rpc('payroll_attendance_summaries', { p_month: month, p_year: year, p_items: items });
  if (error) throw error;
  return data || {};
}

// Removes a month's unpaid payroll rows (paid rows stay) so Generate Payroll can rebuild them.
export async function deleteUnpaidPayrollItems(payrollId) {
  const { data, error } = await supabase.rpc('delete_unpaid_payroll_items', { p_payroll: payrollId });
  if (error) throw error;
  return data;
}

// Unpaid remainders from earlier months that the next payroll must add as arrears: [{ staff_id, source, amount }].
export async function getPendingArrears(schoolId, month, year) {
  const { data, error } = await supabase.rpc('get_pending_arrears', { p_school: schoolId, p_month: month, p_year: year });
  if (error) throw error;
  return data || [];
}

// Marks those remainders as carried into the given payroll (call after its items are created).
export async function carryPayrollArrears(payrollId) {
  const { error } = await supabase.rpc('carry_payroll_arrears', { p_payroll: payrollId });
  if (error) throw error;
}

// Every payroll month with how many rows it has and whether each is paid: [{ month, year, rows: [{ paid }] }].
// The Payroll tab uses this to list only generated months, plus the next one once the latest is fully paid.
export async function getPayrollPeriods(schoolId) {
  const { data, error } = await supabase
    .from('payroll')
    .select('month, year, payroll_items(payment_date), academy_payroll_items(payment_date)')
    .eq('school_id', schoolId);
  if (error) throw error;
  return (data || []).map(p => ({
    month: p.month, year: p.year,
    rows: [...(p.payroll_items || []), ...(p.academy_payroll_items || [])].map(i => ({ paid: !!i.payment_date })),
  }));
}

// Adds a designation to the school's list (or returns the existing one with the same name).
export async function createDesignation(schoolId, name, department) {
  const clean = String(name || '').trim();
  if (!clean) throw new Error('Designation name is required');
  const { data: existing } = await supabase.from('designations').select('id, name, department').eq('school_id', schoolId).ilike('name', clean).limit(1);
  if (existing && existing[0]) return existing[0];
  const { data, error } = await supabase.from('designations')
    .insert({ school_id: schoolId, name: clean, department: String(department || '').trim() || 'Other' })
    .select('id, name, department').single();
  if (error) throw error;
  return data;
}

// ─── School backup and restore (Settings > Account) ──────────────────────────
export async function backupInfo() {
  const { data, error } = await supabase.rpc('backup_info');
  if (error) throw error;
  return data;
}
export async function backupRead(table, offset, limit = 2000) {
  const { data, error } = await supabase.rpc('backup_read', { p_table: table, p_offset: offset, p_limit: limit });
  if (error) throw error;
  return data || [];
}
export async function backupLog(type, size, status, errorMessage = null) {
  const { error } = await supabase.rpc('backup_log', { p_type: type, p_size: size, p_status: status, p_error: errorMessage });
  if (error) throw error;
}
export async function backupHistory() {
  const { data, error } = await supabase.rpc('backup_history');
  if (error) throw error;
  return data || [];
}
export async function restoreStage(session, table, seq, rows) {
  const { error } = await supabase.rpc('restore_stage', { p_session: session, p_table: table, p_seq: seq, p_rows: rows });
  if (error) throw error;
}
export async function restoreApply(session) {
  const { data, error } = await supabase.rpc('restore_apply', { p_session: session, p_confirm: 'RESTORE' });
  if (error) throw error;
  return data;
}
